import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog as MuiDialog,
  Box,
  DialogContent,
  DialogTitle,
  Button as MuiButton,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../../Reducer/SnackbarReducer';
import {
  closeLoading,
  showLoading,
} from '../../../../../Reducer/LoadingReducer';
import { BaseInfoActions } from '../../../../../Reducer/BaseInfoReducer';
import useFileUpload from '../../../../../hooks/useFileUpload';
import { useSave } from '../../../../../hooks/useSaveN';
import useDelete from '../../../../../hooks/useDeleteN';
import RiskAssessment from './RiskAssessment';
import {
  useSearch1,
  useSearch7,
  useSave1,
  useSave2,
  useDelete1,
} from './RiskAssessment_QUERY';
import { NormalizeGetRisBasic01R6Rows } from '../../RIS_BASIC_01/RIS_BASIC_01_QUERY';
import { resetAddButtonError } from '../../../../../Reducer/StatusFlagsReducer';
import StandardModelModal from './modals/StandardModelModal';
import { GetRiskAssessmentStandardModelColumnDefs } from './RiskAssessment_StandardModel_ColumnDefs';
import Commons from '../../../../../Services/Common/Common';
import { apiRequest } from '../../../../../Services/Common/ApiRequest';
import { mapParamsBySchema } from '../../../../../util';
import { useSearch1 as useWorkerOpinionSearch1 } from '../../../../../Scripts/RiskNearMiss/RIN_BASIC/RIN_BASIC_QUERY';
import { mapGetAcsBasic02R1RowToGrid } from '../../../../../Scripts/RiskNearMiss/RIN_BASIC/RIN_BASIC_Schema';
import PhotoReuseLayerModal from './modals/PhotoReuseLayerModal';
import {
  ApplyRiskDetailOverlayToProcesses,
  BuildFiRiskMatrixDerivedPatch,
  MapGetRisCustom02R1ToProcesses,
  MergeSafetyProcessListWithRiskDetails,
  CreateEmptyDetailRow,
  RiskAssessment_Schema,
  ExtractRiskMatrixListFromRisCustomR1Raw,
  ExtractRsMethodFromSafety01Raw,
  IsFrequencyIntensityRsMethod,
  EnrichProcessesReductionMeasuresOptions,
  BuildReductionMeasuresOptions,
  BuildTodaySafetyOptions,
} from './RiskAssessment_Schema';
import { RIS_CUSTOM_Schema } from '../RIS_CUSTOM_Schema';
import {
  MapGetRisCustom01R1ResponseToForm,
  MapGetRisCustom01R2ResponseToProcessModal,
  NormalizeDepCdKey,
  SafetyInfo_Schema,
} from '../Step01_SafetyInfo/SafetyInfo_Schema';
import {
  useSearch1 as useSafetyInfoSearch1,
  useSave1 as useSafetyInfoSave1,
  useSave2 as useSafetyInfoSave2,
} from '../Step01_SafetyInfo/SafetyInfo_QUERY';
import {
  Norm,
  GroupExcelRowsByProcessNm,
  MatchProcessCandidates,
  ResolveMasterGongjong,
  FindDepCdByNm,
  FindDepNmByCd,
  ExtractGongJongCdFromSaveU1Response,
  CollectAmbiguousExcelGroups,
  BuildU2ProcessItem,
} from './RiskAssessment_ExcelImportHelpers';
import ExcelProcessAmbiguityDialog from './modals/ExcelProcessAmbiguityDialog';
import ExcelDeptSelectDialog from './modals/ExcelDeptSelectDialog';
import { useWaitDashboard1 } from '../Step00_Wait/Wait_QUERY';
import { useRisCustomWorkflow } from '../RisCustomWorkflowContext';
import {
  WAIT_STEP_SEQ_IMPROVEMENT,
  WAIT_STEP_SEQ_RISK_ASSESSMENT,
} from '../Step00_Wait/Wait_Schema';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import Global from '../../../../../Services/Common/Global';

const SCREEN_MENU_ID = 'RiskEvaluation';
/** 저장 파일명: YYYYMMDDHHmmss_원본파일명 */
const BuildSaveName = (fileName = '') => {
  const baseName = String(fileName || 'image').replace(/^.*[/\\]/, '');
  const ts = dayjs().format('YYYYMMDDHHmmss');
  return `${ts}_${baseName}`;
};

/** 개선 전·후 저장명이 위 규칙이 아니면(구데이터 등) 원본 파일명 기준으로 보정 */
const EnsureRiskImageSaveName = (saveName, originalFileName) => {
  const raw = String(saveName ?? '').trim();
  if (/^\d{14}_/.test(raw)) return raw;
  return BuildSaveName(originalFileName || raw || 'image');
};

const Pad2 = (n) => String(n).padStart(2, '0');
// 디버깅: 엑셀 다운로드/업로드 분기 확인
const DEBUG_EXCEL_RISK = true;

/**
 * ExcelJS `workbook.xlsx.load` 실패 시 원인 추적용 (RiskAssessmentF 전용).
 * `Cannot read properties of undefined (reading 'company')` 등.
 */
async function DiagnoseXlsxForExcelJsLoad(buffer) {
  const out = {
    bufferByteLength: buffer?.byteLength ?? 0,
    docPropsAppXmlExists: false,
    docPropsAppXmlLength: 0,
    hasClosingPropertiesTag: false,
    hasPrefixedPropertiesRoot: false,
    docPropsAppXmlSnippet: '',
    zipParseError: null,
    zipPathSample: [],
  };
  if (!buffer || !buffer.byteLength) {
    out.note = 'arrayBuffer 비어 있음';
    return out;
  }
  try {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(buffer);
    const paths = Object.keys(zip.files).filter((p) => !zip.files[p].dir);
    out.zipPathSample = paths.slice(0, 60);
    const appKey = 'docProps/app.xml';
    if (!paths.includes(appKey)) {
      out.note =
        'docProps/app.xml 없음 → AppXform 파싱 모델 미생성 → appProperties undefined 가능';
      return out;
    }
    out.docPropsAppXmlExists = true;
    const xml = await zip.file(appKey).async('string');
    out.docPropsAppXmlLength = xml.length;
    out.hasClosingPropertiesTag = /<\/[\w.:-]*Properties\s*>/i.test(xml);
    out.hasPrefixedPropertiesRoot = /<[A-Za-z_][\w.:-]*:Properties\b/.test(xml);
    out.docPropsAppXmlSnippet = xml.slice(0, 500).replace(/\s+/g, ' ').trim();
    if (out.hasPrefixedPropertiesRoot) {
      out.note =
        'docProps/app.xml 루트가 접두사 Properties(ep: 등) → ExcelJS AppXform 미인식 → appProperties undefined 가능 (한셀 등)';
    } else if (!out.hasClosingPropertiesTag) {
      out.note =
        'docProps/app.xml 에 닫는 Properties 태그 없음 → ExcelJS appProperties undefined 가능';
    }
  } catch (err) {
    out.zipParseError = err?.message ?? String(err);
  }
  return out;
}

const MinimalDocPropsAppXmlForExcelJs = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
  <Application>Microsoft Excel</Application>
  <DocSecurity>0</DocSecurity>
  <ScaleCrop>false</ScaleCrop>
  <LinksUpToDate>false</LinksUpToDate>
  <SharedDoc>false</SharedDoc>
  <HyperlinksChanged>false</HyperlinksChanged>
  <AppVersion>16.0000</AppVersion>
</Properties>`;

function NeedsDocPropsAppXmlReplacement(xml) {
  const t = String(xml ?? '').trim();
  if (t === '') return true;
  if (/<[A-Za-z_][\w.:-]*:Properties\b/.test(t)) return true;
  if (!/<\/[\w.:-]*Properties\s*>/i.test(t)) return true;
  return false;
}

function RepairDocPropsAppXmlString(xml) {
  if (xml == null) return xml;
  const t = String(xml).trim();
  if (t === '') return xml;
  if (/<\/[\w.:-]*Properties\s*>/i.test(t)) return xml;
  const prefixed = t.match(/<([\w]+):Properties\b[^>]*>/i);
  if (prefixed) {
    return `${t}</${prefixed[1]}:Properties>`;
  }
  if (/<Properties\b[^>]*>/i.test(t)) {
    return `${t}</Properties>`;
  }
  return xml;
}

const SPREADSHEET_ML_NS =
  'http://schemas.openxmlformats.org/spreadsheetml/2006/main';

/**
 * 한셀 등: 루트가 `x:sst`, `x:worksheet` 처럼 접두사 + 스프레드시트 ML 일 때
 * ExcelJS 가 기대하는 무접두사 로컬명으로 통일.
 */
function NormalizeSpreadsheetMlPrefixedRootXml(xml, rootLocalName) {
  const t = String(xml);
  const m = t.match(new RegExp(`<([A-Za-z_][\\w.:-]*):${rootLocalName}\\b`));
  if (!m) return xml;
  const p = m[1];
  const esc = p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  let out = t;
  const xmlnsPrefixed = new RegExp(
    `\\sxmlns:${esc}="${SPREADSHEET_ML_NS.replace(/\//g, '\\/')}"`,
    'g'
  );
  out = out.replace(xmlnsPrefixed, '');
  out = out.replace(
    new RegExp(`</${esc}:([A-Za-z][\\w.]*)\\s*>`, 'g'),
    '</$1>'
  );
  out = out.replace(new RegExp(`<${esc}:([A-Za-z][\\w.]*)\\b`, 'g'), '<$1');
  const hasDefaultNs = new RegExp(
    `<${rootLocalName}[^>]*\\sxmlns\\s*=\\s*["']http:\\/\\/schemas\\.openxmlformats\\.org\\/spreadsheetml\\/2006\\/main["']`
  );
  if (!hasDefaultNs.test(out)) {
    out = out.replace(
      new RegExp(`<${rootLocalName}\\b`),
      `<${rootLocalName} xmlns="${SPREADSHEET_ML_NS}"`
    );
  }
  return out;
}

function NormalizeSharedStringsXmlForExcelJs(xml) {
  return NormalizeSpreadsheetMlPrefixedRootXml(xml, 'sst');
}

/** RiskAssessmentF 엑셀 업로드 전: docProps/app.xml · sharedStrings 한셀 OOXML 보정 */
async function RepairXlsxBufferForExcelJsLoad(buffer) {
  if (!buffer || !buffer.byteLength) return buffer;
  try {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(buffer);
    let dirty = false;

    const appName = 'docProps/app.xml';
    if (zip.files[appName]) {
      const raw = await zip.file(appName).async('string');
      let next = raw;
      if (NeedsDocPropsAppXmlReplacement(raw)) {
        next = MinimalDocPropsAppXmlForExcelJs;
      } else {
        const fixed = RepairDocPropsAppXmlString(raw);
        next = NeedsDocPropsAppXmlReplacement(fixed)
          ? MinimalDocPropsAppXmlForExcelJs
          : fixed;
      }
      if (next !== raw) {
        zip.file(appName, next);
        dirty = true;
      }
    }

    const ssName = 'xl/sharedStrings.xml';
    if (zip.files[ssName]) {
      const rawSs = await zip.file(ssName).async('string');
      const nextSs = NormalizeSharedStringsXmlForExcelJs(rawSs);
      if (nextSs !== rawSs) {
        zip.file(ssName, nextSs);
        dirty = true;
      }
    }

    const wbPath = 'xl/workbook.xml';
    if (zip.files[wbPath]) {
      const rawWb = await zip.file(wbPath).async('string');
      const nextWb = NormalizeSpreadsheetMlPrefixedRootXml(rawWb, 'workbook');
      if (nextWb !== rawWb) {
        zip.file(wbPath, nextWb);
        dirty = true;
      }
    }

    const stylesPath = 'xl/styles.xml';
    if (zip.files[stylesPath]) {
      const rawSt = await zip.file(stylesPath).async('string');
      const nextSt = NormalizeSpreadsheetMlPrefixedRootXml(rawSt, 'styleSheet');
      if (nextSt !== rawSt) {
        zip.file(stylesPath, nextSt);
        dirty = true;
      }
    }

    const sheetPaths = Object.keys(zip.files).filter((n) =>
      /^xl\/worksheets\/sheet\d+\.xml$/i.test(n)
    );
    for (const sp of sheetPaths) {
      const rawSh = await zip.file(sp).async('string');
      const nextSh = NormalizeSpreadsheetMlPrefixedRootXml(rawSh, 'worksheet');
      if (nextSh !== rawSh) {
        zip.file(sp, nextSh);
        dirty = true;
      }
    }

    if (!dirty) return buffer;
    return await zip.generateAsync({
      type: 'arraybuffer',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 },
    });
  } catch {
    return buffer;
  }
}

/** API DateTime — 로컬 벽시계 문자열 (타임존 시프트 방지) */
const ToLocalDateTimeString = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${Pad2(d.getMonth() + 1)}-${Pad2(d.getDate())}T${Pad2(d.getHours())}:${Pad2(d.getMinutes())}:${Pad2(d.getSeconds())}`;
};

/** 엑셀 셀 → 문자열 (위험성평가 업로드·RiskAssessment3LevelModal과 동일 규칙) */
const ExcelRiskCellToString = (cell) => {
  if (!cell || cell.value == null) return '';
  const v = cell.value;
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number') return String(v);
  if (v instanceof Date) return dayjs(v).format('YYYY-MM-DD');
  if (v && typeof v === 'object' && v.richText) {
    return v.richText
      .map((t) => t?.text || '')
      .join('')
      .trim();
  }
  if (v && typeof v === 'object' && v.text) return String(v.text).trim();
  if (v && typeof v === 'object' && v.result) return String(v.result).trim();
  return String(v).trim();
};

const ParseExcelDateToGrid = (v) => {
  if (v == null || v === '') return null;
  const s = String(v).trim().slice(0, 10);
  if (dayjs(s).isValid()) return dayjs(s).format('YYYY-MM-DD');
  return null;
};

/**
 * 빈도·강도법 엑셀 셀 → 선행 정수만 반영 (예: "1(중)", "2(소)", " 3 " → "1","2","3")
 * @param {unknown} raw
 * @param {{ min: number; max: number }} range
 * @returns {string} 유효하면 숫자 문자열, 아니면 ''
 */
const ParseExcelFiNumericCell = (raw, range) => {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const m = s.match(/^(\d+)/);
  if (!m) return '';
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n < range.min || n > range.max) return '';
  return String(n);
};

/** 가능성(빈도)·중대성(강도) 원문에서 숫자만 뽑아 곱한 위험성 (엑셀 7·14열 무시) */
const CalcFiRiskFromExcelPossImp = (possRaw, impRaw) => {
  const p = ParseExcelFiNumericCell(possRaw, { min: 1, max: 5 });
  const i = ParseExcelFiNumericCell(impRaw, { min: 1, max: 4 });
  if (!p || !i) return '';
  return String(Number(p) * Number(i));
};

/** 평가방법 문자열 정규화 (중점/가운뎃점/공백 차이 흡수) */
const NormalizeRsMethod = (v) =>
  String(v ?? '')
    .replace(/[·ㆍ]/g, '')
    .replace(/\s+/g, '')
    .trim();

// 서버 RS_METHOD 기준 분기 (현재 조회 데이터 값에 맞춤)
const RS_METHOD_FI_N = NormalizeRsMethod('빈도·강도법');

/** 빈도·강도법 여부 (조회 RS_METHOD 값 기준) */
const IsFrequencyIntensityMethod = (v) =>
  NormalizeRsMethod(v) === RS_METHOD_FI_N;

// 3단계 판단법은 default 분기(else)로 처리

/** 엑셀 파싱 행 → 세부 그리드 행 (다운로드 열 순서와 동일) */
const MapExcelParsedRowToDetail = (
  item,
  sriId,
  idx,
  isFrequencyIntensity,
  rsCd,
  forceNew = false
) => {
  const base = CreateEmptyDetailRow(sriId);
  const n = idx + 1;
  const rsdStr = forceNew ? '' : String(item.excelRsdCd ?? '').trim();
  const subStr = forceNew ? '' : String(item.excelSubJobId ?? '').trim();
  const rrdNum = forceNew ? 0 : /^\d+$/.test(subStr) ? Number(subStr) : 0;
  const clientKey =
    rsdStr !== '' ? `rsd-${sriId}-${rsdStr}` : `row-${sriId}-${idx}`;

  return {
    ...base,
    clientKey,
    no: n,
    rowNo: n,
    rsdCd: rsdStr || clientKey,
    rrdId: rrdNum,
    newYn: forceNew ? 'Y' : rsdStr ? 'N' : 'Y',
    rsCd: Number(rsCd) > 0 ? Number(rsCd) : 0,
    sriId,
    categoryComment: String(item.categoryComment ?? '').trim(),
    todaySafety: String(item.todaySafety ?? '').trim(),
    reductionMeasures: String(item.reductionMeasures ?? '').trim(),
    riskLevelJudgment: isFrequencyIntensity
      ? ''
      : String(item.riskLevelJudgment ?? '').trim(),
    riskAfterLevelJudgment: isFrequencyIntensity
      ? ''
      : String(item.riskAfterLevelJudgment ?? '').trim(),
    gaesunExDate: ParseExcelDateToGrid(item.gaesunExDate),
    gaesunComDate: ParseExcelDateToGrid(item.gaesunComDate),
    riskId: String(item.riskId ?? '').trim(),
    todayPossibility: isFrequencyIntensity
      ? ParseExcelFiNumericCell(item.todayPossibility, { min: 1, max: 5 })
      : '',
    todayImportance: isFrequencyIntensity
      ? ParseExcelFiNumericCell(item.todayImportance, { min: 1, max: 4 })
      : '',
    todayCurrentRisk: isFrequencyIntensity
      ? CalcFiRiskFromExcelPossImp(item.todayPossibility, item.todayImportance)
      : '',
    afterPossibility: isFrequencyIntensity
      ? ParseExcelFiNumericCell(item.afterPossibility, { min: 1, max: 5 })
      : '',
    afterImportance: isFrequencyIntensity
      ? ParseExcelFiNumericCell(item.afterImportance, { min: 1, max: 4 })
      : '',
    afterCurrentRisk: isFrequencyIntensity
      ? CalcFiRiskFromExcelPossImp(item.afterPossibility, item.afterImportance)
      : '',
  };
};

/** refetch 결과와 동일 규칙으로 공정+세부 병합 */
const BuildMergedProcessesFromQueries = ({
  safetyRaw,
  searchRawArg,
  assessmentTitle,
  assessmentRsMethod,
  safety01RawForMethod,
  risCustomR1Raw,
  assessmentRsCd,
  riskDetailOverlay,
  standardModelRows,
}) => {
  const patched = MapGetRisCustom01R1ResponseToForm(safetyRaw, {
    assessmentTitle,
  });
  const safetyPl = Array.isArray(patched?.processList) ? patched.processList : [];
  const risk02 =
    searchRawArg != null ? MapGetRisCustom02R1ToProcesses(searchRawArg) : [];
  let next = MergeSafetyProcessListWithRiskDetails(safetyPl, risk02);
  const rsForFiPatch =
    String(assessmentRsMethod ?? '').trim() ||
    ExtractRsMethodFromSafety01Raw(safety01RawForMethod ?? safetyRaw);
  const matrixForFiPatch = ExtractRiskMatrixListFromRisCustomR1Raw(
    risCustomR1Raw,
    assessmentRsCd
  );
  if (IsFrequencyIntensityRsMethod(rsForFiPatch) && matrixForFiPatch.length) {
    next = next.map((proc) => ({
      ...proc,
      details: (Array.isArray(proc.details) ? proc.details : []).map((r) => ({
        ...r,
        ...BuildFiRiskMatrixDerivedPatch(r, matrixForFiPatch),
      })),
    }));
  }
  next = ApplyRiskDetailOverlayToProcesses(
    next,
    riskDetailOverlay,
    assessmentRsCd
  );
  next = EnrichProcessesReductionMeasuresOptions(next, standardModelRows);
  return next;
};

/** 엑셀 해석 결과로 세부행 반영 (append/replace) */
const ApplyExcelDetailsToMergedProcesses = (
  processList,
  resolvedEntries,
  importMode,
  isFi,
  rsCdNum
) => {
  const isReplaceMode = importMode === 'replace';
  const rowsBySri = new Map();
  const rowsByProcessNm = new Map();
  for (const entry of resolvedEntries) {
    rowsByProcessNm.set(Norm(entry.processNm), entry.excelRows ?? []);
    const sid = Number(entry.existingProcess?.sriId) || 0;
    if (sid > 0) rowsBySri.set(sid, entry.excelRows ?? []);
  }

  const deleteCandidates = [];
  let applied = 0;

  const next = (Array.isArray(processList) ? processList : []).map((p) => {
    const sid = Number(p.sriId) || 0;
    let items =
      sid > 0 && rowsBySri.has(sid)
        ? rowsBySri.get(sid)
        : rowsByProcessNm.get(Norm(p.processNm));
    if (!items?.length) return p;
    if (!sid) return p;
    applied += items.length;

    const existing = Array.isArray(p.details) ? p.details : [];
    const baseDetails = isReplaceMode ? [] : existing;
    if (isReplaceMode) {
      existing.forEach((r) => {
        const rsdCd = String(r?.rsdCd ?? '').trim();
        if (/^\d+$/.test(rsdCd)) deleteCandidates.push(r);
      });
    }
    const startNo = baseDetails.length;
    const appended = items.map((it, idx) => {
      const row = MapExcelParsedRowToDetail(
        it,
        sid,
        startNo + idx,
        isFi,
        rsCdNum,
        true
      );
      const nextNo = startNo + idx + 1;
      return { ...row, no: nextNo, rowNo: nextNo };
    });
    return { ...p, details: [...baseDetails, ...appended] };
  });

  return { processes: next, deleteCandidates, applied };
};

const ComputeProcessesNeedingDept = (
  excelGroups,
  processes,
  gongjongList,
  depData,
  ambiguitySelections
) => {
  const needed = [];
  for (const [processNm, rows] of excelGroups) {
    const excelDepNm = Norm(rows?.[0]?.depNm);
    const assessment = MatchProcessCandidates(processNm, excelDepNm, processes);
    let assessmentMatch = assessment.autoMatch;
    if (assessment.ambiguous) {
      const picked = ambiguitySelections?.[processNm];
      if (picked?.kind === 'assessment') {
        assessmentMatch = picked.data ?? null;
      } else {
        continue;
      }
    }
    if (assessmentMatch) continue;

    const master = ResolveMasterGongjong(processNm, excelDepNm, gongjongList);
    let masterMatch = master.match;
    if (master.ambiguous) {
      const picked = ambiguitySelections?.[processNm];
      if (picked?.kind === 'master') masterMatch = picked.data ?? null;
      else continue;
    }
    if (masterMatch) continue;

    const depCd = FindDepCdByNm(excelDepNm, depData);
    if (!depCd) {
      needed.push({ processNm, excelDepNm });
    }
  }
  return needed;
};

const ResolveExcelProcessEntries = async ({
  excelGroups,
  processes,
  gongjongList,
  depData,
  ambiguitySelections,
  deptSelections,
  SaveProcessU1Async,
}) => {
  const resolved = [];
  for (const [processNm, rows] of excelGroups) {
    const excelDepNm = Norm(rows?.[0]?.depNm);
    const assessment = MatchProcessCandidates(processNm, excelDepNm, processes);
    let assessmentMatch = assessment.autoMatch;
    if (assessment.ambiguous) {
      const picked = ambiguitySelections?.[processNm];
      assessmentMatch =
        picked?.kind === 'assessment' ? picked.data ?? null : null;
    }

    let gongJongCd = '';
    let depCd = '';
    let depNm = excelDepNm;
    let existingProcess = assessmentMatch ?? null;

    if (assessmentMatch) {
      gongJongCd = Norm(
        assessmentMatch.gongJongCd ?? assessmentMatch.processId
      );
      depCd = NormalizeDepCdKey(assessmentMatch.depCd);
      depNm = Norm(assessmentMatch.depNm) || depNm;
    } else {
      const master = ResolveMasterGongjong(processNm, excelDepNm, gongjongList);
      let masterMatch = master.match;
      if (master.ambiguous) {
        const picked = ambiguitySelections?.[processNm];
        masterMatch = picked?.kind === 'master' ? picked.data ?? null : null;
      }

      if (masterMatch) {
        gongJongCd = Norm(masterMatch.gongJongCd);
        depCd = NormalizeDepCdKey(masterMatch.depCd);
        depNm = Norm(masterMatch.depNm) || depNm;
      } else {
        depCd =
          FindDepCdByNm(excelDepNm, depData) ||
          NormalizeDepCdKey(deptSelections?.[processNm]?.value);
        depNm =
          Norm(deptSelections?.[processNm]?.label) ||
          FindDepNmByCd(depCd, depData) ||
          excelDepNm;

        if (!depCd) {
          throw new Error(`부서를 선택해 주세요. (공정: ${processNm})`);
        }

        const u1Result = await SaveProcessU1Async([
          { depCd, gongJongNm: processNm },
        ]);
        if (IsFailed(u1Result)) {
          throw new Error(
            u1Result?.Message ??
              u1Result?.message ??
              `공정 등록에 실패했습니다. (${processNm})`
          );
        }
        gongJongCd = ExtractGongJongCdFromSaveU1Response(u1Result);
        if (!gongJongCd) {
          const found = gongjongList.find(
            (g) =>
              Norm(g.gongJongNm) === processNm &&
              NormalizeDepCdKey(g.depCd) === depCd
          );
          gongJongCd = Norm(found?.gongJongCd);
        }
        if (!gongJongCd) {
          throw new Error(
            `공정 코드를 확인하지 못했습니다. (${processNm})`
          );
        }
        if (!depNm) depNm = FindDepNmByCd(depCd, depData);
      }
    }

    resolved.push({
      processNm,
      excelDepNm,
      excelRows: rows,
      gongJongCd,
      depCd,
      depNm,
      existingProcess,
    });
  }
  return resolved;
};

const BuildProcessListForU2 = (
  resolved,
  currentProcesses,
  importMode,
  safetyProcessList = []
) => {
  const isReplace = importMode === 'replace';
  const currentList = Array.isArray(currentProcesses) ? currentProcesses : [];
  const safetyList = Array.isArray(safetyProcessList) ? safetyProcessList : [];

  const FindEnrichedProcess = (candidate) => {
    if (!candidate) return null;
    const sid = Number(candidate.sriId) || 0;
    const cd = Norm(candidate.processId ?? candidate.gongJongCd);
    if (sid > 0) {
      const bySid = safetyList.find((p) => Number(p.sriId) === sid);
      if (bySid) return { ...bySid, ...candidate };
    }
    if (cd) {
      const byCd = safetyList.find((p) => Norm(p.processId) === cd);
      if (byCd) return { ...byCd, ...candidate };
    }
    return candidate;
  };

  if (isReplace) {
    return resolved.map((r, idx) => {
      const existing = FindEnrichedProcess(
        r.existingProcess ??
          currentList.find(
            (p) =>
              Norm(p.processNm) === Norm(r.processNm) &&
              (Norm(p.depNm) === Norm(r.depNm) ||
                !Norm(r.depNm) ||
                !Norm(p.depNm))
          )
      );
      return BuildU2ProcessItem(r, existing, idx);
    });
  }

  const result = currentList.map((p, idx) => {
    const enriched = FindEnrichedProcess(p) ?? p;
    return BuildU2ProcessItem(
      {
        processNm: enriched.processNm ?? p.processNm,
        gongJongCd: enriched.processId ?? p.gongJongCd ?? p.processId,
        depCd: enriched.depCd ?? p.depCd,
        depNm: enriched.depNm ?? p.depNm,
      },
      enriched,
      idx
    );
  });

  const existingCodes = new Set(
    result.map((p) => Norm(p.processId ?? p.gongJongCd)).filter(Boolean)
  );

  for (const r of resolved) {
    if (r.existingProcess) continue;
    if (r.gongJongCd && existingCodes.has(r.gongJongCd)) continue;
    result.push(
      BuildU2ProcessItem(r, null, result.length)
    );
    if (r.gongJongCd) existingCodes.add(r.gongJongCd);
  }

  return result.map((p, i) => ({ ...p, processNo: i + 1, selected: i === 0 }));
};

const IsFailed = (result) => {
  if (!result) return true;
  if (result.IsSuccess === false || result.isSuccess === false) return true;
  if (Array.isArray(result?.data) && result.data.length > 0) {
    return result.data.some(
      (r) => r?.isSuccess === false || r?.IsSuccess === false
    );
  }
  if (Array.isArray(result?.Data) && result.Data.length > 0) {
    return result.Data.some(
      (r) => r?.isSuccess === false || r?.IsSuccess === false
    );
  }
  if (result.severity === 'error') return true;
  return false;
};

/**
 * 위험성평가 실시 기능단 — GetRisCustom02R1 / SaveRisCustom02U1 / DeleteRisCustom02D1
 */
function RiskAssessmentF({
  onCancel,
  onSaved,
  assessmentTitle = '',
  assessmentRsCd = null,
  assessmentRsMethod = '',
}) {
  const { riskDetailOverlay, clearRiskDetailOverlay } =
    useRisCustomWorkflow() ?? {};
  const dispatch = useDispatch();
  const { userInfo, riskWorkerOpinionMenuActive } = useSelector(
    (s) => s.BaseInfo
  );
  const searchInfo = useSelector((s) => s.SearchInfo);

  const [processes, setProcesses] = useState([]);
  /** 툴바 새로고침 등: 쿼리 캐시 참조가 같아도 세부행 병합(useEffect)을 다시 돌리기 위한 트리거 */
  const [detailDataMergeNonce, setDetailDataMergeNonce] = useState(0);
  const [selectedSriId, setSelectedSriId] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const selectedRowNosRef = useRef(new Set());
  const lastCheckedIndexRef = useRef(null);
  const detailGridRef = useRef(null);
  const excelImportInputRef = useRef(null);
  const [deletedRowsDetail, setDeletedRowsDetail] = useState([]);
  const [, setChangedRowDataDetail] = useState([]);
  const pendingStepSeqRef = useRef(WAIT_STEP_SEQ_RISK_ASSESSMENT);
  /** useSave onSuccessRefresh — 임시저장: temp, 저장 후 다음: next */
  const pendingAfterSaveKindRef = useRef('temp');
  const pendingSaveCountRef = useRef({ saveCount: 0, deleteCount: 0 });

  // 사진 레이어 팝업
  const [photoLayerOpen, setPhotoLayerOpen] = useState(false);
  const [photoLayerCtx, setPhotoLayerCtx] = useState({
    clientKey: null,
    field: null, // riskImageFilePath | afterRiskImageFilePath
  });

  const [recentBeforePhotos, setRecentBeforePhotos] = useState([]);
  const [recentAfterPhotos, setRecentAfterPhotos] = useState([]);

  /** 표준모델 요인 (GetRisBasic01R6) — RIS_BASIC_01과 동일 */
  const [standardModelOpen, setStandardModelOpen] = useState(false);
  const [standardModelRows, setStandardModelRows] = useState([]);
  const [modalRowData4, setModalRowData4] = useState([]);
  const [selectedModalRowData4, setSelectedModalRowData4] = useState([]);
  const selectedModalRowNosRef4 = useRef(new Set());
  const lastCheckedIndexRef3 = useRef(null);
  const gridRef5 = useRef(null);
  const searchRefStandardModel = useRef({ modalSearchText: '' });
  const [excelImportModeOpen, setExcelImportModeOpen] = useState(false);
  const [pendingExcelImportFile, setPendingExcelImportFile] = useState(null);
  const [excelAmbiguityOpen, setExcelAmbiguityOpen] = useState(false);
  const [excelAmbiguityGroups, setExcelAmbiguityGroups] = useState([]);
  const [excelDeptSelectOpen, setExcelDeptSelectOpen] = useState(false);
  const [excelDeptSelectCtx, setExcelDeptSelectCtx] = useState(null);
  const [excelMasterDepData, setExcelMasterDepData] = useState([]);
  const ambiguityResolverRef = useRef(null);
  const deptSelectResolverRef = useRef(null);
  /** refetch 직후 useEffect 병합 1회 건너뛰기 (엑셀 세부 반영 유지) */
  const skipNextProcessMergeRef = useRef(false);

  const workerOpinionReceptionStorageKey = useMemo(() => {
    const c = searchInfo?.custCd;
    const r = assessmentRsCd;
    if (c == null || c === '' || r == null || Number(r) <= 0) return null;
    return `RisWorkerOpinionReception_${String(c)}_${String(r)}`;
  }, [searchInfo?.custCd, assessmentRsCd]);

  /** 근로자 의견 받기 활성화 상태 */
  const [workerOpinionReceptionActive, setWorkerOpinionReceptionActive] =
    useState(false);
  /** 근로자 의견 받기 저장 로딩 상태 */
  const [
    workerOpinionReceptionSaveLoading,
    setWorkerOpinionReceptionSaveLoading,
  ] = useState(false);

  useEffect(() => {
    if (!workerOpinionReceptionStorageKey) {
      setWorkerOpinionReceptionActive(false);
      return;
    }
    setWorkerOpinionReceptionActive(
      sessionStorage.getItem(workerOpinionReceptionStorageKey) === '1'
    );
  }, [workerOpinionReceptionStorageKey]);

  // photoBefore/photoAfter 레이어에서 Before/After 모두 재사용 가능하도록 합친 목록
  const combinedRecentPhotos = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of [
      ...(recentBeforePhotos ?? []),
      ...(recentAfterPhotos ?? []),
    ]) {
      const fileName = p?.fileName;
      const fileKey = String(fileName ?? '')
        .trim()
        .toLowerCase();
      // 파일명이 없는 경우는 saveName으로 대체 dedupe
      const saveName = p?.saveName;
      const fallbackKey = String(saveName ?? '')
        .trim()
        .toLowerCase();
      const keyToUse = fileKey || fallbackKey;
      if (!keyToUse || seen.has(keyToUse)) continue;
      seen.add(keyToUse);
      out.push(p);
      if (out.length >= 200) break;
    }
    return out;
  }, [recentBeforePhotos, recentAfterPhotos]);

  const { data: waitDashboardData, refetch: RefetchWaitDashboard } =
    useWaitDashboard1({
      custCd: searchInfo?.custCd,
      rsCd: assessmentRsCd,
      titleOverride: assessmentTitle,
      enabled: Number(assessmentRsCd) > 0,
    });

  /** GetRisCustom00R1 대시보드 스텝을 가져와서 유동적으로 사용  */
  const workflowStepsFromApi = useMemo(() => {
    const live = waitDashboardData?.steps;
    if (!Array.isArray(live) || live.length === 0) return [];
    return live.map((s, i) => {
      const id = s.routeStepId || s.stepId || `step-${i}`;
      return {
        stepId: id,
        routeStepId: s.routeStepId || s.stepId,
        title: String(s.title ?? s.stepNm ?? `단계 ${i + 1}`),
        status: s.status,
      };
    });
  }, [waitDashboardData?.steps]);

  const {
    data: safety01Raw,
    isFetching: safety01Fetching,
    refetch: RefetchSafety01,
  } = useSafetyInfoSearch1({
    rsCd: assessmentRsCd,
    enabled: Number(assessmentRsCd) > 0,
    /** 실시 화면 진입마다 MatrixList 포함 최신 GetRisCustom01R1 확보 */
    refetchOnMount: 'always',
  });

  const {
    data: searchRaw,
    refetch: RefetchSearch1,
    isFetching: searchFetching,
  } = useSearch1({
    rsCd: assessmentRsCd,
    enabled: Number(assessmentRsCd) > 0,
  });

  const { runAsync: SaveSafetyInfoU2Async } = useSafetyInfoSave1();
  const { runAsync: SaveProcessRegisterU1Async } = useSafetyInfoSave2();

  const risCustomR1MatrixEnabled =
    Number(assessmentRsCd) > 0 &&
    Boolean(String(searchInfo?.custCd ?? '').trim());

  const {
    data: risCustomR1Raw,
    isFetching: risCustomR1Fetching,
    refetch: RefetchRisCustomR1,
  } = useQuery({
    queryKey: [
      'RiskEvaluation',
      'RiskAssessment',
      'getRisCustomR1',
      'matrix',
      assessmentRsCd,
      searchInfo?.custCd,
      searchInfo?.endDate,
      searchInfo?.startDate,
    ],
    enabled: risCustomR1MatrixEnabled,
    refetchOnMount: 'always',
    queryFn: async () => {
      const params = mapParamsBySchema(RIS_CUSTOM_Schema.mapper.search1, {
        search: {
          custCd: searchInfo?.custCd || '',
          endDate:
            searchInfo?.endDate || searchInfo?.startDate || '',
          searchText: '',
        },
      });
      return apiRequest(
        'post',
        Global.urls.RiskEvaluation.getRisCustomR1,
        params
      );
    },
  });

  const { run: Search7, runAsync: Search7Async } = useSearch7({
    onSuccess: (rows) => {
      const arr = Array.isArray(rows) ? rows : [];
      const normalized = NormalizeGetRisBasic01R6Rows(arr);
      setStandardModelRows(normalized);
      selectedModalRowNosRef4.current = new Set();
      lastCheckedIndexRef3.current = null;
      setModalRowData4(normalized);
    },
  });

  useEffect(() => {
    if (!searchInfo?.custCd) return;
    Search7({ search: { ...searchInfo, searchText: '' } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInfo?.custCd]);

  /** GridCheckBox는 (단일 행, index)를 넘기므로 그리드 API 기준 전체 선택 행으로 맞춤 */
  const handleRowSelection3 = useCallback(() => {
    const api = gridRef5.current?.api;
    if (api) setSelectedModalRowData4(api.getSelectedRows());
  }, []);

  const columnDefsStandardModel = useMemo(
    () =>
      GetRiskAssessmentStandardModelColumnDefs({
        gridRef5,
        modalRowData4,
        selectedModalRowNosRef4,
        lastCheckedIndexRef3,
        handleRowSelection3,
      }),
    [modalRowData4, handleRowSelection3]
  );

  /** 공정 목록은 Step01 안전보건정보(GetRisCustom01R1) 기준, 세부행은 GetRisCustom02R1 병합 */
  useEffect(() => {
    if (skipNextProcessMergeRef.current) {
      skipNextProcessMergeRef.current = false;
      return;
    }
    const patched = MapGetRisCustom01R1ResponseToForm(safety01Raw, {
      assessmentTitle,
    });
    const safetyPl = Array.isArray(patched?.processList)
      ? patched.processList
      : [];
    const risk02 =
      searchRaw != null ? MapGetRisCustom02R1ToProcesses(searchRaw) : [];
    let next = MergeSafetyProcessListWithRiskDetails(safetyPl, risk02);
    const rsForFiPatch =
      String(assessmentRsMethod ?? '').trim() ||
      ExtractRsMethodFromSafety01Raw(safety01Raw);
    const matrixForFiPatch = ExtractRiskMatrixListFromRisCustomR1Raw(
      risCustomR1Raw,
      assessmentRsCd
    );
    if (IsFrequencyIntensityRsMethod(rsForFiPatch) && matrixForFiPatch.length) {
      next = next.map((proc) => ({
        ...proc,
        details: (Array.isArray(proc.details) ? proc.details : []).map((r) => ({
          ...r,
          ...BuildFiRiskMatrixDerivedPatch(r, matrixForFiPatch),
        })),
      }));
    }
    next = ApplyRiskDetailOverlayToProcesses(
      next,
      riskDetailOverlay,
      assessmentRsCd
    );
    next = EnrichProcessesReductionMeasuresOptions(next, standardModelRows);
    setProcesses(next);
    setSelectedSriId((prev) => {
      if (prev != null && next.some((p) => p.sriId === prev)) return prev;
      return next[0]?.sriId ?? null;
    });
    setSelectedKeys([]);
  }, [
    safety01Raw,
    searchRaw,
    assessmentTitle,
    assessmentRsMethod,
    riskDetailOverlay,
    assessmentRsCd,
    detailDataMergeNonce,
    risCustomR1Raw,
    standardModelRows,
  ]);

  // 사진 재사용 레이어 "최근 사진"은 근로자 의견 청취(WorkerOpinionModal)가 쓰는
  // 동일 API(RIN_BASIC) 응답에서 Before/After 이미지를 뽑아 사용한다.
  const photoLayerOpenRef = useRef(false);
  useEffect(() => {
    photoLayerOpenRef.current = photoLayerOpen;
  }, [photoLayerOpen]);

  useWorkerOpinionSearch1({
    searchKey: [
      searchInfo?.custCd,
      searchInfo?.depCd,
      '',
      searchInfo?.year,
      searchInfo?.month,
      searchInfo?.gubun,
      photoLayerOpen,
    ],
    getSearchContext: () => ({
      search: { ...(searchInfo ?? {}), gongjongCd: '' },
    }),
    onSuccess: (rows) => {
      if (!photoLayerOpenRef.current) return;
      // WorkerOpinionModal과 동일하게 Grid 매핑을 한 번 거쳐
      // preFilePath/preSaveName 같은 이미지 필드가 채워진 뒤
      // RiskAssessment detail 필드로 재매핑한다.
      const gridRows = (rows || [])
        .map(mapGetAcsBasic02R1RowToGrid)
        .filter(Boolean);

      const buildList = (which) => {
        const out = [];
        const seen = new Set();
        const MAX = 200;

        for (const r of gridRows) {
          const isBefore = which === 'before';
          const fileNamesStr = isBefore ? r.preFileNames : r.postFileNames;
          const saveNamesStr = isBefore ? r.preSaveName : r.postSaveName;
          const folder = isBefore ? r.preFilePath : r.postFilePath;

          const fileNames = String(fileNamesStr ?? '')
            .split('|')
            .map((s) => s.trim())
            .filter(Boolean);
          const saveNames = String(saveNamesStr ?? '')
            .split('|')
            .map((s) => s.trim())
            .filter(Boolean);

          if (!folder) continue;

          const maxLen = Math.max(fileNames.length, saveNames.length);
          for (let i = 0; i < maxLen; i += 1) {
            const fileName =
              fileNames[i] ?? fileNames[fileNames.length - 1] ?? '';
            const saveName =
              saveNames[i] ?? saveNames[saveNames.length - 1] ?? '';
            if (!saveName) continue;
            // 같은 파일명은 중복 제거
            const fileKey = String(fileName ?? '')
              .trim()
              .toLowerCase();
            const saveKey = String(saveName ?? '')
              .trim()
              .toLowerCase();
            const keyToUse = fileKey || saveKey;
            if (!keyToUse) continue;
            if (seen.has(keyToUse)) continue;

            const finalFilePath = `${folder}/${saveName}`;
            seen.add(keyToUse);
            out.push({
              fileName: fileName ?? '',
              saveName,
              previewFilePath: '',
              finalFilePath,
            });

            if (out.length >= MAX) break;
          }

          if (out.length >= MAX) break;
        }

        return out;
      };

      setRecentBeforePhotos(buildList('before'));
      setRecentAfterPhotos(buildList('after'));
    },
  });

  const { runAsync: Save1Async } = useSave1();
  const { runAsync: Save2Async } = useSave2();
  const deleteMutation = useDelete1();
  const {
    SaveLogicPreview: SaveLogicPreviewBefore,
    SaveLogicFile: SaveLogicFileBefore,
  } = useFileUpload({
    oldFilePathField: 'riskImageFilePath',
    newFileNameField: 'riskImageSaveName',
    getTargetPath: (_, custCd) =>
      `RisBasicCustom/${custCd}/RiskAssessment/Before`,
  });
  const {
    SaveLogicPreview: SaveLogicPreviewAfter,
    SaveLogicFile: SaveLogicFileAfter,
  } = useFileUpload({
    oldFilePathField: 'afterRiskImageFilePath',
    newFileNameField: 'afterRiskImageSaveName',
    getTargetPath: (_, custCd) =>
      `RisBasicCustom/${custCd}/RiskAssessment/After`,
  });

  const currentDetails = useMemo(() => {
    const p = processes.find((x) => x.sriId === selectedSriId);
    return Array.isArray(p?.details) ? p.details : [];
  }, [processes, selectedSriId]);

  const deleteHandler1 = useDelete({
    gridRef: detailGridRef,
    deleteMutation,
    updateDeleted: setDeletedRowsDetail,
    updateRowData: (rows) => {
      setProcesses((prev) =>
        prev.map((p) => {
          if (p.sriId !== selectedSriId) return p;
          return { ...p, details: Array.isArray(rows) ? rows : [] };
        })
      );
    },
    deletedRowData: deletedRowsDetail,
    rowData: currentDetails,
    setChangedRowData: setChangedRowDataDetail,
  });

  const { run: Save1 } = useSave({
    menuId: SCREEN_MENU_ID,
    requiredLabel: null,
    schema: { fields: RiskAssessment_Schema.fields },
    getChangedRows: () => {
      const stepSeq = pendingStepSeqRef.current;
      const uid =
        userInfo?.userId ?? userInfo?.UserId ?? userInfo?.loginId ?? '';
      const deletedRsdCdSet = new Set(
        (deletedRowsDetail ?? [])
          .map((d) => String(d?.rsdCd ?? '').trim())
          .filter((v) => /^\d+$/.test(v))
      );
      return processes
        .filter((p) => Number(p.sriId))
        .map((p) => ({
          editYn: 'Y',
          rsCd: assessmentRsCd,
          userId: uid,
          stepSeq,
          processNm: p.processNm ?? '',
          items: (Array.isArray(p.details) ? p.details : [])
            .filter((d) => {
              const rsdCd = String(d?.rsdCd ?? '').trim();
              if (!/^\d+$/.test(rsdCd)) return true; // 신규행은 저장 대상 유지
              return !deletedRsdCdSet.has(rsdCd);
            })
            .map((d) => ({
              ...d,
            })),
        }));
    },
    getDeletedRows: () =>
      Array.isArray(deletedRowsDetail)
        ? deletedRowsDetail.filter((r) => /^\d+$/.test(String(r?.rsdCd ?? '')))
        : [],
    requestDelete: deleteHandler1.DeleteLogic,
    fileSave: async (changedRows) => {
      const custCd = String(searchInfo?.custCd ?? '');
      const movedBeforeMap = new Map(); // saveName -> finalPath
      const movedAfterMap = new Map(); // saveName -> finalPath

      for (const processRow of changedRows) {
        const items = Array.isArray(processRow?.items) ? processRow.items : [];
        for (const item of items) {
          if (item?.riskImagePreviewFilePath && item?.riskImageSaveName) {
            const saveName = EnsureRiskImageSaveName(
              item.riskImageSaveName,
              item.riskImageFileName
            );
            item.riskImageSaveName = saveName;
            const finalPath = `RisBasicCustom/${custCd}/RiskAssessment/Before/${saveName}`;

            // 같은 saveName이 여러 행에 재사용되는 경우 moveAndReplaceFile 중복 호출 방지
            if (!movedBeforeMap.has(saveName)) {
              const sourcePreviewPath = item?.riskImagePreviewFilePath ?? '';
              const oldFilePathRaw = item?.riskImageFilePath ?? '';
              const oldFilePath =
                (oldFilePathRaw &&
                  String(oldFilePathRaw).includes('PreviewFiles')) ||
                oldFilePathRaw === sourcePreviewPath
                  ? ''
                  : oldFilePathRaw;

              await SaveLogicFileBefore(
                {
                  riskImageFilePath: oldFilePath,
                  riskImageSaveName: saveName,
                  previewFilePath: item.riskImagePreviewFilePath,
                },
                custCd
              );
              movedBeforeMap.set(saveName, finalPath);
            }

            item.riskImageFilePath = finalPath;
            item.riskImagePreviewFilePath = '';
          }
          if (
            item?.afterRiskImagePreviewFilePath &&
            item?.afterRiskImageSaveName
          ) {
            const saveName = EnsureRiskImageSaveName(
              item.afterRiskImageSaveName,
              item.afterRiskImageFileName
            );
            item.afterRiskImageSaveName = saveName;
            const finalPath = `RisBasicCustom/${custCd}/RiskAssessment/After/${saveName}`;

            if (!movedAfterMap.has(saveName)) {
              const sourcePreviewPath =
                item?.afterRiskImagePreviewFilePath ?? '';
              const oldFilePathRaw = item?.afterRiskImageFilePath ?? '';
              const oldFilePath =
                (oldFilePathRaw &&
                  String(oldFilePathRaw).includes('PreviewFiles')) ||
                oldFilePathRaw === sourcePreviewPath
                  ? ''
                  : oldFilePathRaw;

              await SaveLogicFileAfter(
                {
                  afterRiskImageFilePath: oldFilePath,
                  afterRiskImageSaveName: saveName,
                  previewFilePath: item.afterRiskImagePreviewFilePath,
                },
                custCd
              );
              movedAfterMap.set(saveName, finalPath);
            }

            item.afterRiskImageFilePath = finalPath;
            item.afterRiskImagePreviewFilePath = '';
          }
        }
      }

      // 최근 사진 리스트의 final 경로 갱신(다음 재사용을 위해)
      if (custCd) {
        if (movedBeforeMap.size) {
          setRecentBeforePhotos((prev) => {
            const next = (Array.isArray(prev) ? prev : []).map((p) => {
              if (!p?.saveName) return p;
              if (!movedBeforeMap.has(p.saveName)) return p;
              return {
                ...p,
                finalFilePath: movedBeforeMap.get(p.saveName),
                previewFilePath: '',
              };
            });
            return next;
          });
        }
        if (movedAfterMap.size) {
          setRecentAfterPhotos((prev) => {
            const next = (Array.isArray(prev) ? prev : []).map((p) => {
              if (!p?.saveName) return p;
              if (!movedAfterMap.has(p.saveName)) return p;
              return {
                ...p,
                finalFilePath: movedAfterMap.get(p.saveName),
                previewFilePath: '',
              };
            });
            return next;
          });
        }
      }
    },
    requestSave: async (rows) => {
      const saveDetailData = [];
      for (const row of rows) {
        const res = await Save1Async([row]);
        if (IsFailed(res)) {
          saveDetailData.push({
            isSuccess: false,
            Message: res?.Message ?? res?.message,
          });
          break;
        }
        saveDetailData.push({ isSuccess: true });
      }
      return { data: saveDetailData };
    },
    onSuccessRefresh: async ({ saveDetailData }) => {
      clearRiskDetailOverlay?.();
      await Promise.all([
        RefetchSearch1(),
        RefetchSafety01(),
        Search7Async({ search: { ...searchInfo, searchText: '' } }),
        ...(risCustomR1MatrixEnabled ? [RefetchRisCustomR1()] : []),
      ]);
      const list = saveDetailData ?? [];
      const allOk = list.length > 0 && list.every((d) => d.isSuccess === true);
      if (allOk) {
        const { saveCount, deleteCount } = pendingSaveCountRef.current ?? {};
        dispatch(
          showSnackbar({
            message: `저장 ${Number(saveCount) || 0}건, 삭제 ${Number(deleteCount) || 0}건 처리되었습니다.`,
            type: 'success',
          })
        );
      }
      if (pendingAfterSaveKindRef.current === 'next' && allOk) {
        onSaved?.();
      }
    },
  });

  const HandleSelectProcess = useCallback((sriId) => {
    setSelectedSriId(sriId);
    setSelectedKeys([]);
  }, []);

  const HandleRequestPhoto = useCallback(({ clientKey, field }) => {
    if (!clientKey || !field) return;
    if (field !== 'riskImageFilePath' && field !== 'afterRiskImageFilePath')
      return;
    setPhotoLayerCtx({ clientKey, field });
    setPhotoLayerOpen(true);
  }, []);

  const currentCellPhoto = useMemo(() => {
    const { clientKey, field } = photoLayerCtx ?? {};
    if (!clientKey || !field) return { path: '', fileName: '' };
    const p = processes.find((x) => x.sriId === selectedSriId);
    const row = (Array.isArray(p?.details) ? p.details : []).find(
      (d) => d?.clientKey === clientKey
    );
    const path = row?.[field] ?? '';
    const fileName =
      field === 'riskImageFilePath'
        ? (row?.riskImageFileName ?? '')
        : (row?.afterRiskImageFileName ?? '');
    return { path, fileName };
  }, [photoLayerCtx, processes, selectedSriId]);

  const HandlePickRecentPhoto = useCallback(
    (entry) => {
      const { clientKey, field } = photoLayerCtx ?? {};
      if (!clientKey || !field || !entry) return;
      const isBefore = field === 'riskImageFilePath';
      const displayPath = entry?.finalFilePath ?? entry?.previewFilePath ?? '';
      const sourcePath = entry?.previewFilePath ?? entry?.finalFilePath ?? '';
      if (!displayPath) return;

      const pickedSaveName = EnsureRiskImageSaveName(
        entry?.saveName,
        entry?.fileName
      );
      setProcesses((prev) =>
        prev.map((p) => {
          if (p.sriId !== selectedSriId) return p;
          return {
            ...p,
            details: (Array.isArray(p.details) ? p.details : []).map((r) => {
              if (r.clientKey !== clientKey) return r;
              if (isBefore) {
                return {
                  ...r,
                  riskImageFilePath: displayPath,
                  riskImageFileName: entry?.fileName ?? '',
                  riskImageSaveName: pickedSaveName,
                  // moveAndReplaceFile은 sourcePreviewPath(previewFilePath)가 필요하므로
                  // 서버에서 바로 온 파일도 sourcePath로 넘긴다.
                  riskImagePreviewFilePath: sourcePath,
                  ...(r.newYn === 'Y' ? {} : { editYn: 'Y' }),
                };
              }
              return {
                ...r,
                afterRiskImageFilePath: displayPath,
                afterRiskImageFileName: entry?.fileName ?? '',
                afterRiskImageSaveName: pickedSaveName,
                afterRiskImagePreviewFilePath: sourcePath,
                ...(r.newYn === 'Y' ? {} : { editYn: 'Y' }),
              };
            }),
          };
        })
      );

      setPhotoLayerOpen(false);
    },
    [photoLayerCtx, selectedSriId]
  );

  const HandlePhotoPicked = useCallback(
    async ({ clientKey, field, file }) => {
      if (!clientKey || !field || !file) return;
      const isBefore = field === 'riskImageFilePath';
      const saveName = BuildSaveName(file.name);
      const previewPath = isBefore
        ? await SaveLogicPreviewBefore(file)
        : await SaveLogicPreviewAfter(file);
      if (!previewPath) return;
      setProcesses((prev) =>
        prev.map((p) => ({
          ...p,
          details: (Array.isArray(p.details) ? p.details : []).map((r) => {
            if (r.clientKey !== clientKey) return r;
            if (isBefore) {
              return {
                ...r,
                riskImageFilePath: previewPath,
                riskImageFileName: file.name,
                riskImageSaveName: saveName,
                riskImagePreviewFilePath: previewPath,
                ...(r.newYn === 'Y' ? {} : { editYn: 'Y' }),
              };
            }
            return {
              ...r,
              afterRiskImageFilePath: previewPath,
              afterRiskImageFileName: file.name,
              afterRiskImageSaveName: saveName,
              afterRiskImagePreviewFilePath: previewPath,
              ...(r.newYn === 'Y' ? {} : { editYn: 'Y' }),
            };
          }),
        }))
      );

      // 즉시 재사용을 위해 현재 선택(미리보기)도 리스트에 반영
      const entry = {
        fileName: file.name,
        saveName,
        previewFilePath: previewPath,
        finalFilePath: '',
      };

      const upsertRecent = (prev, nextEntry) => {
        const cur = Array.isArray(prev) ? prev : [];
        const filtered = cur.filter((x) => x?.saveName !== nextEntry?.saveName);
        return [nextEntry, ...filtered].slice(0, 24);
      };

      if (isBefore) {
        setRecentBeforePhotos((prev) => upsertRecent(prev, entry));
      } else {
        setRecentAfterPhotos((prev) => upsertRecent(prev, entry));
      }
    },
    [SaveLogicPreviewAfter, SaveLogicPreviewBefore]
  );

  const HandleDetailSelectionKeysChange = useCallback((keys) => {
    setSelectedKeys(Array.isArray(keys) ? keys : []);
  }, []);

  const DeleteNewRow = useCallback(
    (row) => {
      // 신규 행 삭제는 서버 대상이 아니므로 UI/로컬 상태를 즉시 정리
      deleteHandler1?.DeleteNewRow?.(row);
      const n = row?.no;
      if (n == null) return;
      setProcesses((prev) =>
        prev.map((p) => {
          if (p.sriId !== selectedSriId) return p;
          return {
            ...p,
            details: p.details.filter((r) => r.no !== n),
          };
        })
      );
    },
    [selectedSriId, deleteHandler1]
  );

  const RestoreRow = useCallback(
    (row) => {
      deleteHandler1?.RestoreRow?.(row);
    },
    [deleteHandler1]
  );

  const HandleCheckboxRowSelection = useCallback(() => {}, []);

  const HandleAddRow = useCallback(() => {
    if (selectedSriId == null) return;
    const row = CreateEmptyDetailRow(selectedSriId);
    setProcesses((prev) =>
      prev.map((p) => {
        if (p.sriId !== selectedSriId) return p;
        // 신규행을 최상단에 표시하기 위해 0번째에 삽입하고,
        // 정렬/표시용 순번(rowNo/no)을 일괄 재정렬한다.
        const nextDetails = Array.isArray(p.details) ? p.details : [];
        return {
          ...p,
          details: [
            { ...row, rowNo: 1, no: 1 },
            ...nextDetails.map((d) => ({
              ...d,
              rowNo: (Number(d?.rowNo) || 0) + 1,
              no: (Number(d?.no) || 0) + 1,
            })),
          ],
        };
      })
    );
    const api = detailGridRef.current?.api;
    if (!api) return;
    api.deselectAll?.();
    api.ensureIndexVisible?.(0, 'top');
    api.getDisplayedRowAtIndex?.(0)?.setSelected?.(true, false);
    // POL_BASIC_01과 동일하게 "추가 직후 최상단 행 선택" UX 제공
    // requestAnimationFrame(() => {
    // });
  }, [selectedSriId, detailGridRef]);

  const HandleOpenStandardModel = useCallback(() => {
    if (selectedSriId == null) {
      dispatch(
        showSnackbar({ message: '공정을 선택하세요.', type: 'warning' })
      );
      return;
    }
    setSelectedModalRowData4([]);
    setStandardModelOpen(true);
    void Search7();
  }, [selectedSriId, dispatch, Search7]);

  const HandleConfirmStandardModel = useCallback(
    (selectedRows) => {
      const list = Array.isArray(selectedRows)
        ? selectedRows
        : [selectedRows].filter(Boolean);
      if (!list.length || selectedSriId == null) return;
      setProcesses((prev) =>
        prev.map((p) => {
          if (p.sriId !== selectedSriId) return p;
          let nextDetails = [...p.details];
          for (const it of list) {
            const row = CreateEmptyDetailRow(selectedSriId);
            const nextNo = nextDetails.length + 1;
            const reductionMeasuresOptions = BuildTodaySafetyOptions(it);
            nextDetails.push({
              ...row,
              rowNo: nextNo,
              no: nextNo,
              categoryComment: String(
                it.categoryComment ?? it.CategoryComment ?? ''
              ).trim(),
              // 위험성 감소대책은 상세 셀 클릭 시 선택 팝업에서 지정
              reductionMeasures: '',
              reductionMeasuresOptions,
              condolenceNo: String(
                it.condolenceNo ??
                  it.CondolenceNo ??
                  it.sttLprvsNo ??
                  it.SttLprvsNo ??
                  ''
              ).trim(),
              relatedEvidence: String(
                it.sttLprvsTtlNm ??
                  it.SttLprvsTtlNm ??
                  it.relatedEvidence ??
                  it.RelatedEvidence ??
                  ''
              ).trim(),
            });
          }
          return { ...p, details: nextDetails };
        })
      );
      setStandardModelOpen(false);
      setSelectedModalRowData4([]);
      dispatch(
        showSnackbar({
          message: '표준모델 요인이 반영되었습니다.',
          type: 'success',
        })
      );
    },
    [selectedSriId, dispatch]
  );

  const HandleRefresh = useCallback(async () => {
    if (!Number(assessmentRsCd)) {
      dispatch(
        showSnackbar({
          message: '유효한 평가코드(RS_CD)가 없습니다.',
          type: 'warning',
        })
      );
      return;
    }
    const custCd = searchInfo?.custCd;
    try {
      const tasks = [RefetchSafety01(), RefetchSearch1()];
      if (searchInfo?.custCd) {
        tasks.push(
          Search7Async({ search: { ...searchInfo, searchText: '' } })
        );
      }
      if (custCd != null && custCd !== '' && Number(assessmentRsCd) > 0) {
        tasks.push(RefetchWaitDashboard());
        tasks.push(RefetchRisCustomR1());
      }
      await Promise.all(tasks);
      setDetailDataMergeNonce((n) => n + 1);
      dispatch(showSnackbar({ message: '조회되었습니다.', type: 'success' }));
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '조회에 실패했습니다.',
          type: 'error',
        })
      );
    }
  }, [
    RefetchSafety01,
    RefetchSearch1,
    RefetchWaitDashboard,
    RefetchRisCustomR1,
    Search7Async,
    assessmentRsCd,
    dispatch,
    searchInfo?.custCd,
  ]);

  const HandleDeleteRows = useCallback(() => {
    const keySet = new Set(selectedKeys);
    const selectedRows = (currentDetails ?? []).filter((r) => keySet.has(r.no));
    if (!selectedRows.length) {
      dispatch(
        showSnackbar({ message: '삭제할 행을 선택하세요.', type: 'warning' })
      );
      return;
    }

    const validExistingRows = selectedRows.filter(
      (r) => /^\d+$/.test(String(r?.rsdCd ?? '')) && r?.newYn !== 'Y'
    );
    const newRows = selectedRows.filter((r) => r?.newYn === 'Y');

    // 신규 행은 즉시 로컬 제거 (서버 삭제 대상 아님)
    if (newRows.length) {
      newRows.forEach((row) => {
        deleteHandler1?.DeleteNewRow?.(row);
      });
      setProcesses((prev) =>
        prev.map((p) => {
          if (p.sriId !== selectedSriId) return p;
          return {
            ...p,
            details: p.details.filter((r) => !keySet.has(r.no)),
          };
        })
      );
    }

    // 기존 행은 삭제예정 표시만
    if (validExistingRows.length) {
      deleteHandler1.MarkedForDeletion(validExistingRows);
    }

    setSelectedKeys([]);
  }, [currentDetails, selectedKeys, dispatch, deleteHandler1, selectedSriId]);

  /** 임시저장: 현재 단계 StepSeq 유지(위험성평가 실시 = 2) — useSave + useSave1 */
  const HandleTempSave = useCallback(async () => {
    if (!Number(assessmentRsCd)) {
      dispatch(
        showSnackbar({
          message: '유효한 평가코드(RS_CD)가 없습니다.',
          type: 'warning',
        })
      );
      return;
    }
    const toSave = processes.filter((p) => Number(p.sriId));
    if (!toSave.length) {
      dispatch(
        showSnackbar({
          message: '저장할 공정이 없습니다.',
          type: 'warning',
        })
      );
      return;
    }
    const deletedRsdCdSet = new Set(
      (deletedRowsDetail ?? [])
        .map((d) => String(d?.rsdCd ?? '').trim())
        .filter((v) => /^\d+$/.test(v))
    );
    const saveCount = toSave.reduce((acc, p) => {
      const cnt = (Array.isArray(p.details) ? p.details : []).filter((d) => {
        const rsdCd = String(d?.rsdCd ?? '').trim();
        if (!/^\d+$/.test(rsdCd)) return true;
        return !deletedRsdCdSet.has(rsdCd);
      }).length;
      return acc + cnt;
    }, 0);
    const deleteCount = deletedRsdCdSet.size;
    pendingSaveCountRef.current = { saveCount, deleteCount };
    pendingStepSeqRef.current = WAIT_STEP_SEQ_RISK_ASSESSMENT;
    pendingAfterSaveKindRef.current = 'temp';
    await Save1();
  }, [Save1, assessmentRsCd, deletedRowsDetail, dispatch, processes]);

  /**
   * 저장 후 다음: StepSeq=3(개선보고서), 모든 공정에 세부 1건 이상 필요
   */
  const HandleSaveAndNext = useCallback(async () => {
    if (!Number(assessmentRsCd)) {
      dispatch(
        showSnackbar({
          message: '유효한 평가코드(RS_CD)가 없습니다.',
          type: 'warning',
        })
      );
      return;
    }
    const list = Array.isArray(processes) ? processes : [];
    if (list.length === 0) {
      dispatch(
        showSnackbar({
          message: '등록된 공정이 없습니다. 이전 단계에서 공정을 등록하세요.',
          type: 'warning',
        })
      );
      return;
    }
    const deletedNoSet = new Set(
      (deletedRowsDetail ?? []).map((d) => d?.no).filter((n) => n != null)
    );
    const missing = list.filter((p) => {
      if (!(Number(p.sriId) > 0)) return false;
      const activeDetails = (Array.isArray(p.details) ? p.details : []).filter(
        (d) => !deletedNoSet.has(d?.no)
      );
      return activeDetails.length === 0;
    });
    if (missing.length > 0) {
      dispatch(
        showSnackbar({
          message:
            '모든 공정에 위험성평가 세부항목을 1건 이상 등록한 뒤 진행할 수 있습니다.',
          type: 'warning',
        })
      );
      return;
    }
    const deletedRsdCdSet = new Set(
      (deletedRowsDetail ?? [])
        .map((d) => String(d?.rsdCd ?? '').trim())
        .filter((v) => /^\d+$/.test(v))
    );
    const saveCount = list.reduce((acc, p) => {
      if (!(Number(p.sriId) > 0)) return acc;
      const cnt = (Array.isArray(p.details) ? p.details : []).filter((d) => {
        const rsdCd = String(d?.rsdCd ?? '').trim();
        if (!/^\d+$/.test(rsdCd)) return true;
        return !deletedRsdCdSet.has(rsdCd);
      }).length;
      return acc + cnt;
    }, 0);
    const deleteCount = deletedRsdCdSet.size;
    pendingSaveCountRef.current = { saveCount, deleteCount };
    pendingStepSeqRef.current = WAIT_STEP_SEQ_IMPROVEMENT;
    pendingAfterSaveKindRef.current = 'next';
    await Save1();
  }, [Save1, assessmentRsCd, dispatch, processes, deletedRowsDetail]);

  // =========================
  // 엑셀 다운로드 (RIS_BASIC_CUSTOM)
  // =========================

  function getTemplatePath(method) {
    const base = `${Global.file.useUrl}files/ExcellExportFrom/RiskEvaluation/`;
    if (method === '빈도·강도법')
      return base + '위험가능성과 중대성을 조합한 빈도ㆍ강도법_custom.xlsx';
    if (method === '위험성 수준 3단계 판단법')
      return base + '위험성 수준 3단계 판단법_custom.xlsx';
    return null;
  }

  function getCurrentFormattedDateTime(_type) {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    if (_type === 'data') {
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
    if (_type === 'file') {
      return `${year}${month}${day}_${hours}${minutes}${seconds}`;
    }
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  const currentRsMethod = useMemo(
    () =>
      assessmentRsMethod ||
      ExtractRsMethodFromSafety01Raw(safety01Raw) ||
      '위험성 수준 3단계 판단법',
    [assessmentRsMethod, safety01Raw]
  );

  const riskMatrixList = useMemo(
    () =>
      ExtractRiskMatrixListFromRisCustomR1Raw(risCustomR1Raw, assessmentRsCd),
    [risCustomR1Raw, assessmentRsCd]
  );

  const fiPatchFields = useMemo(
    () =>
      new Set([
        'todayPossibility',
        'todayImportance',
        'afterPossibility',
        'afterImportance',
      ]),
    []
  );

  const HandlePatchRow = useCallback(
    (clientKey, field, value) => {
      setProcesses((prev) =>
        prev.map((p) => {
          if (p.sriId !== selectedSriId) return p;
          return {
            ...p,
            details: p.details.map((r) => {
              if (r.clientKey !== clientKey) return r;
              let next = { ...r, [field]: value };
              if (
                IsFrequencyIntensityRsMethod(currentRsMethod) &&
                fiPatchFields.has(field)
              ) {
                next = {
                  ...next,
                  ...BuildFiRiskMatrixDerivedPatch(next, riskMatrixList),
                };
              }
              if (r.newYn !== 'Y') {
                next.editYn = 'Y';
              }
              return next;
            }),
          };
        })
      );
    },
    [selectedSriId, currentRsMethod, riskMatrixList, fiPatchFields]
  );

  /** 위험성 감소대책 팝업 — 표준모델 addActn 옵션 조회·행에 캐시 */
  const HandleResolveReductionMeasuresOptions = useCallback(
    async (row) => {
      let rows = standardModelRows;
      if (!rows.length && searchInfo?.custCd) {
        try {
          const fetched = await Search7Async({
            search: { ...searchInfo, searchText: '' },
          });
          const arr = Array.isArray(fetched) ? fetched : [];
          rows = NormalizeGetRisBasic01R6Rows(arr);
          setStandardModelRows(rows);
          setModalRowData4(rows);
        } catch (e) {
          console.log(e);
        }
      }

      const options = BuildReductionMeasuresOptions(row, rows);
      const clientKey = String(row?.clientKey ?? '').trim();
      if (clientKey && options.length) {
        setProcesses((prev) =>
          prev.map((p) => {
            if (p.sriId !== selectedSriId) return p;
            return {
              ...p,
              details: (p.details || []).map((d) =>
                d.clientKey === clientKey
                  ? { ...d, reductionMeasuresOptions: options }
                  : d
              ),
            };
          })
        );
      }
      return options;
    },
    [standardModelRows, searchInfo, Search7Async, selectedSriId]
  );

  /** matrixList가 늦게 도착해도 이미 입력된 빈도·강도 행에 LevelNm·점수 파생값 반영(editYn 변경 없음) */
  useEffect(() => {
    if (!IsFrequencyIntensityRsMethod(currentRsMethod)) return;
    const matrix = riskMatrixList ?? [];
    if (!matrix.length) return;
    setProcesses((prev) =>
      prev.map((p) => ({
        ...p,
        details: (p.details || []).map((r) => {
          const patch = BuildFiRiskMatrixDerivedPatch(r, matrix);
          const unchanged =
            String(r.todayCurrentRisk ?? '') ===
              String(patch.todayCurrentRisk ?? '') &&
            String(r.afterCurrentRisk ?? '') ===
              String(patch.afterCurrentRisk ?? '') &&
            String(r.riskLevelJudgment ?? '') ===
              String(patch.riskLevelJudgment ?? '') &&
            String(r.riskAfterLevelJudgment ?? '') ===
              String(patch.riskAfterLevelJudgment ?? '');
          if (unchanged) return r;
          return { ...r, ...patch };
        }),
      }))
    );
  }, [riskMatrixList, currentRsMethod]);

  const ExcelDownload3Step = async (headerData, detailData) => {
    const workbook = new ExcelJS.Workbook();
    const templateUrl = getTemplatePath('위험성 수준 3단계 판단법');
    if (DEBUG_EXCEL_RISK) {
      // eslint-disable-next-line no-console
      console.log('[RiskAssessmentF][ExcelDownload3Step]', {
        detailCount: Array.isArray(detailData) ? detailData.length : null,
        rsMethod: headerData?.rsMethod,
      });
    }
    if (!templateUrl) {
      dispatch(
        showSnackbar({
          message: '엑셀 템플릿 경로를 찾지 못했습니다.',
          type: 'warning',
        })
      );
      return;
    }

    const response = await fetch(templateUrl);
    const arrayBuffer = await response.arrayBuffer();
    await workbook.xlsx.load(arrayBuffer);

    const worksheet = workbook.worksheets[0];
    if (!worksheet) {
      dispatch(
        showSnackbar({
          message: '템플릿 시트를 불러오지 못했습니다.',
          type: 'warning',
        })
      );
      return;
    }

    // 내보낼 데이터가 없으면: 템플릿(양식)만 다운로드
    if (!Array.isArray(detailData) || detailData.length === 0) {
      const formatted = getCurrentFormattedDateTime('data');
      const userNm = userInfo.userNm ?? '';
      worksheet.getCell('A1').value =
        `엑셀생성자: ${userNm}, 생성일시: ${formatted}`;

      const dateTimeStr = getCurrentFormattedDateTime('file');
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const fileName = `위험성평가_위험성 수준 3단계 판단법_${dateTimeStr}.xlsx`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      return;
    }

    const excelTag = (str) =>
      String(str ?? '')
        .replace(/\s/g, '_')
        .replace(/-/g, '_')
        .replace(/\(/g, '_')
        .replace(/\)/g, '_')
        .replace(/\//g, '_')
        .replace(/\\/g, '_');

    // 해당 위험성평가에 등록된 부서·공정·세부공정만 콤보에 노출 (detailData 기준)
    let allDepts = [];
    let deptListForFill = [];

    const deptOrder = [];
    const deptSeen = new Set();
    const procMap = {};

    for (const r of detailData) {
      const dep = String(r.depNm ?? '').trim();
      const proc = String(r.gongJongNm ?? '').trim();
      const job = String(r.jobNm ?? '').trim();
      if (!dep) continue;
      if (!deptSeen.has(dep)) {
        deptSeen.add(dep);
        deptOrder.push(dep);
      }
      if (!procMap[dep]) procMap[dep] = [];
      let pe = procMap[dep].find((p) => p.gongJongNm === proc);
      if (!pe) {
        pe = { gongJongNm: proc, jobNms: [] };
        procMap[dep].push(pe);
      }
      if (job && !pe.jobNms.includes(job)) pe.jobNms.push(job);
    }

    allDepts = deptOrder;
    deptListForFill = deptOrder.map((dep) => ({
      uniqueDeptNm: dep,
      deptTagName: excelTag(dep) || '부서',
      processes: (procMap[dep] || []).map((p) => ({
        gongJongNm: p.gongJongNm,
        jobNms: p.jobNms || [],
      })),
    }));

    // --- [STEP 1] Data_Master 시트(숨김) 생성 및 이름 정의 ---
    const sheetMaster = workbook.addWorksheet('Data_Master');
    sheetMaster.state = 'hidden';

    sheetMaster.getCell('A1').value = '부서목록';
    allDepts.forEach((name, i) => {
      sheetMaster.getCell(`A${i + 2}`).value = name;
    });

    if (allDepts.length > 0) {
      workbook.definedNames.add(
        `Data_Master!$A$2:$A$${allDepts.length + 1}`,
        '부서목록'
      );
    }

    let currentCol = 2;
    deptListForFill.forEach(({ deptTagName, processes }) => {
      const deptColLetter = sheetMaster.getColumn(currentCol).letter;
      sheetMaster.getCell(1, currentCol).value = deptTagName;
      processes.forEach((proc, rowIdx) => {
        sheetMaster.getCell(rowIdx + 2, currentCol).value =
          proc.gongJongNm ?? '';
      });

      const lastRow = processes.length > 0 ? processes.length + 1 : 2;
      workbook.definedNames.add(
        `Data_Master!$${deptColLetter}$2:$${deptColLetter}$${lastRow}`,
        deptTagName
      );

      currentCol++;

      processes.forEach((proc) => {
        const procTag = excelTag(proc.gongJongNm) || '공정';
        const procTagName = `${deptTagName}_${procTag}`;
        const jobNms = proc.jobNms || [];

        sheetMaster.getCell(1, currentCol).value = procTagName;
        jobNms.forEach((name, stIdx) => {
          sheetMaster.getCell(stIdx + 2, currentCol).value = name;
        });

        const subColLetter = sheetMaster.getColumn(currentCol).letter;
        const subLastRow = jobNms.length > 0 ? jobNms.length + 1 : 2;
        const rangeRef = `Data_Master!$${subColLetter}$2:$${subColLetter}$${subLastRow}`;
        workbook.definedNames.add(rangeRef, procTagName);
        currentCol++;
      });
    });

    const emptyColLetter = sheetMaster.getColumn(currentCol).letter;
    sheetMaster.getCell(1, currentCol).value = '';
    workbook.definedNames.add(
      `Data_Master!$${emptyColLetter}$1:$${emptyColLetter}$1`,
      '세부공정_빈목록'
    );

    // 세부공정명 C열 없음 — C열 콤보 보정 루프 생략

    // --- [STEP 2] 템플릿 시트에 평가 정보 및 콤보박스 적용 ---
    const formatted = getCurrentFormattedDateTime('data');
    const userNm = userInfo.userNm ?? '';

    worksheet.getCell('A1').value =
      `엑셀생성자: ${userNm}, 생성일시: ${formatted}`;
    const a3Cell = worksheet.getCell('A3');
    a3Cell.value =
      '※ 부서명/공정명을 변경한 경우, 해당 행의 공정명을 지운 뒤 다시 선택하세요.';
    a3Cell.font = { bold: true, color: { argb: 'FFCC0000' } };

    // 📌 헬퍼 함수: 엑셀 수식용 SUBSTITUTE 중첩 생성 (excelTag와 일치시킴)
    const buildExcelSubst = (col, row) => {
      const cell = `$${col}${row}`;
      return `SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(${cell}, " ", "_"), "-", "_"), "(", "_"), ")", "_"), "/", "_"), CHAR(92), "_")`;
    };

    // --- [STEP 2] 템플릿 시트에 평가 정보 및 콤보박스 적용 ---
    for (let i = 6; i <= 500; i++) {
      // 1. 부서명 (A열)
      worksheet.getCell(`A${i}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ['부서목록'],
      };

      // 2. 공정명 (B열) - A열 부서명에 종속
      const deptInDir = `INDIRECT(${buildExcelSubst('A', i)})`;
      worksheet.getCell(`B${i}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`=IF($A${i}<>"", ${deptInDir}, 세부공정_빈목록)`],
      };

      // 5열(E): 위험성 수준판단(riskLevelJudgment) — 상, 중, 하 (세부공정 C열 제거 반영)
      worksheet.getCell(i, 5).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ['"상,중,하"'],
      };

      // 10열(J): 개선 후 위험성 수준판단(riskAfterLevelJudgment) — 상, 중, 하
      worksheet.getCell(i, 10).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ['"상,중,하"'],
      };
    }

    // --- [STEP 3] 상세 데이터 삽입 ---
    const startRow = 6;
    detailData.forEach((item, idx) => {
      const rowNum = startRow + idx;
      const row = worksheet.getRow(rowNum);

      // 1 부서, 2 공정, 3~(세부공정 열 없음) 유해위험…
      row.getCell(1).value = item.depNm ?? '';
      row.getCell(2).value = item.gongJongNm ?? '';
      row.getCell(3).value = item.categoryComment ?? '';
      row.getCell(4).value = item.todaySafety ?? '';
      row.getCell(5).value = item.riskLevelJudgment ?? '';
      row.getCell(6).value = item.reductionMeasures ?? '';

      row.getCell(7).value =
        item.gaesunExDate && dayjs(item.gaesunExDate).isValid()
          ? dayjs(item.gaesunExDate).format('YYYY-MM-DD')
          : '';
      row.getCell(8).value =
        item.gaesunComDate && dayjs(item.gaesunComDate).isValid()
          ? dayjs(item.gaesunComDate).format('YYYY-MM-DD')
          : '';

      row.getCell(9).value = item.riskId ?? '';
      row.getCell(10).value = item.riskAfterLevelJudgment ?? '';
      row.getCell(11).value = item.subJobId ?? '';
      row.getCell(12).value = item.rsdCd ?? '';

      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });

    // 11열(subJobId), 12열(rsdCd) 숨김
    worksheet.getColumn(11).hidden = true;
    worksheet.getColumn(12).hidden = true;

    // --- [STEP 4] 저장 및 다운로드 ---
    const dateTimeStr = getCurrentFormattedDateTime('file');
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const fileName = `위험성평가_위험성 수준 3단계 판단법_${dateTimeStr}.xlsx`;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  };

  const ExcelDownloadFrequencyIntensity = async (headerData, detailData) => {
    const workbook = new ExcelJS.Workbook();
    const templateUrl = getTemplatePath('빈도·강도법');
    if (DEBUG_EXCEL_RISK) {
      // eslint-disable-next-line no-console
      console.log('[RiskAssessmentF][ExcelDownloadFrequencyIntensity]', {
        detailCount: Array.isArray(detailData) ? detailData.length : null,
        rsMethod: headerData?.rsMethod,
      });
    }
    if (!templateUrl) {
      dispatch(
        showSnackbar({
          message: '엑셀 템플릿 경로를 찾지 못했습니다.',
          type: 'warning',
        })
      );
      return;
    }

    const response = await fetch(templateUrl);
    const arrayBuffer = await response.arrayBuffer();
    await workbook.xlsx.load(arrayBuffer);

    const worksheet = workbook.worksheets[0];
    if (!worksheet) {
      dispatch(
        showSnackbar({
          message: '템플릿 시트를 불러오지 못했습니다.',
          type: 'warning',
        })
      );
      return;
    }

    // 내보낼 데이터가 없으면: 템플릿(양식)만 다운로드
    if (!Array.isArray(detailData) || detailData.length === 0) {
      const formatted = getCurrentFormattedDateTime('data');
      const userNm = userInfo.userNm ?? '';
      worksheet.getCell('A1').value =
        `엑셀생성자: ${userNm}, 생성일시: ${formatted}`;

      worksheet.getCell('F3').value = '';

      const dateTimeStr = getCurrentFormattedDateTime('file');
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const fileName = `위험가능성과 중대성을 조합한 빈도ㆍ강도법_${dateTimeStr}.xlsx`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      return;
    }

    const excelTag = (str) =>
      String(str ?? '')
        .replace(/\s/g, '_')
        .replace(/-/g, '_')
        .replace(/\(/g, '_')
        .replace(/\)/g, '_')
        .replace(/\//g, '_')
        .replace(/\\/g, '_');

    // Data_Master 구성 (detailData 기반)
    let allDepts = [];
    let deptListForFill = [];

    const deptOrder = [];
    const deptSeen = new Set();
    const procMap = {};
    for (const r of detailData) {
      const dep = String(r.depNm ?? '').trim();
      const proc = String(r.gongJongNm ?? '').trim();
      const job = String(r.jobNm ?? '').trim();
      if (!dep) continue;
      if (!deptSeen.has(dep)) {
        deptSeen.add(dep);
        deptOrder.push(dep);
      }
      if (!procMap[dep]) procMap[dep] = [];
      let pe = procMap[dep].find((p) => p.gongJongNm === proc);
      if (!pe) {
        pe = { gongJongNm: proc, jobNms: [] };
        procMap[dep].push(pe);
      }
      if (job && !pe.jobNms.includes(job)) pe.jobNms.push(job);
    }

    allDepts = deptOrder;
    deptListForFill = deptOrder.map((dep) => ({
      uniqueDeptNm: dep,
      deptTagName: excelTag(dep) || '부서',
      processes: (procMap[dep] || []).map((p) => ({
        gongJongNm: p.gongJongNm,
        jobNms: p.jobNms || [],
      })),
    }));

    const sheetMaster = workbook.addWorksheet('Data_Master');
    sheetMaster.state = 'hidden';
    sheetMaster.getCell('A1').value = '부서목록';
    allDepts.forEach((name, i) => {
      sheetMaster.getCell(`A${i + 2}`).value = name;
    });
    if (allDepts.length > 0) {
      workbook.definedNames.add(
        `Data_Master!$A$2:$A$${allDepts.length + 1}`,
        '부서목록'
      );
    }

    let currentCol = 2;
    deptListForFill.forEach(({ deptTagName, processes }) => {
      const deptColLetter = sheetMaster.getColumn(currentCol).letter;
      sheetMaster.getCell(1, currentCol).value = deptTagName;
      processes.forEach((proc, rowIdx) => {
        sheetMaster.getCell(rowIdx + 2, currentCol).value =
          proc.gongJongNm ?? '';
      });
      const lastRow = processes.length > 0 ? processes.length + 1 : 2;
      workbook.definedNames.add(
        `Data_Master!$${deptColLetter}$2:$${deptColLetter}$${lastRow}`,
        deptTagName
      );
      currentCol++;

      processes.forEach((proc) => {
        const procTag = excelTag(proc.gongJongNm) || '공정';
        const procTagName = `${deptTagName}_${procTag}`;
        const jobNms = proc.jobNms || [];
        sheetMaster.getCell(1, currentCol).value = procTagName;
        jobNms.forEach((name, stIdx) => {
          sheetMaster.getCell(stIdx + 2, currentCol).value = name;
        });
        const subColLetter = sheetMaster.getColumn(currentCol).letter;
        const subLastRow = jobNms.length > 0 ? jobNms.length + 1 : 2;
        const rangeRef = `Data_Master!$${subColLetter}$2:$${subColLetter}$${subLastRow}`;
        workbook.definedNames.add(rangeRef, procTagName);
        currentCol++;
      });
    });

    const emptyColLetter = sheetMaster.getColumn(currentCol).letter;
    sheetMaster.getCell(1, currentCol).value = '';
    workbook.definedNames.add(
      `Data_Master!$${emptyColLetter}$1:$${emptyColLetter}$1`,
      '세부공정_빈목록'
    );

    // 📌 헬퍼 함수: 엑셀 수식용 SUBSTITUTE 중첩 생성 (excelTag와 일치시킴)
    const buildExcelSubst = (col, row) => {
      const cell = `$${col}${row}`;
      return `SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(${cell}, " ", "_"), "-", "_"), "(", "_"), ")", "_"), "/", "_"), CHAR(92), "_")`;
    };

    // --- [STEP 2] 템플릿 시트에 평가 정보 및 콤보박스 적용 ---
    for (let i = 6; i <= 500; i++) {
      worksheet.getCell(`A${i}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ['부서목록'],
      };

      const deptInDir = `INDIRECT(${buildExcelSubst('A', i)})`;
      worksheet.getCell(`B${i}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`=IF($A${i}<>"", ${deptInDir}, 세부공정_빈목록)`],
      };

      // 세부공정명 C열 없음 — C열 콤보 생략

      // 빈도·강도법: 가능성(빈도) 1~5, 중대성(강도) 1~4 (세부공정 열 제거 반영)
      worksheet.getCell(i, 5).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ['"1,2,3,4,5"'],
      };
      worksheet.getCell(i, 6).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ['"1,2,3,4"'],
      };
      worksheet.getCell(i, 12).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ['"1,2,3,4,5"'],
      };
      worksheet.getCell(i, 13).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ['"1,2,3,4"'],
      };
    }

    const formatted = getCurrentFormattedDateTime('data');
    const userNm = userInfo.userNm ?? '';
    worksheet.getCell('A1').value =
      `엑셀생성자: ${userNm}, 생성일시: ${formatted}`;

    const a3CellFi = worksheet.getCell('A3');
    a3CellFi.value =
      '※ 부서명/공정명을 변경한 경우, 해당 행의 공정명을 지운 뒤 다시 선택하세요.';
    a3CellFi.font = { bold: true, color: { argb: 'FFCC0000' } };

    worksheet.getCell('F3').value = '';

    const startRow = 6;
    const columnKeys = [
      'depNm',
      'gongJongNm',
      'categoryComment',
      'todaySafety',
      'todayPossibility',
      'todayImportance',
      'todayCurrentRisk',
      'reductionMeasures',
      'gaesunExDate',
      'gaesunComDate',
      'riskId',
      'afterPossibility',
      'afterImportance',
      'afterCurrentRisk',
    ];

    detailData.forEach((item, index) => {
      const row = worksheet.getRow(startRow + index);
      columnKeys.forEach((key, i) => {
        const cell = row.getCell(i + 1);
        let val = item[key];
        if (key === 'gaesunExDate' || key === 'gaesunComDate') {
          val =
            val && dayjs(val).isValid()
              ? dayjs(val).format('YYYY-MM-DD')
              : (val ?? '');
        }
        cell.value = val ?? '';
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
      row.getCell(15).value = item.subJobId ?? '';
      row.getCell(16).value = item.rsdCd ?? '';
    });

    worksheet.getColumn(15).hidden = true;
    worksheet.getColumn(16).hidden = true;

    const dateTimeStr = getCurrentFormattedDateTime('file');
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const fileName = `위험가능성과 중대성을 조합한 빈도ㆍ강도법_${dateTimeStr}.xlsx`;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  };

  const ExcelDownload1 = async () => {
    try {
      const rsMethodRaw = currentRsMethod;
      const rsMethodNormalized = NormalizeRsMethod(rsMethodRaw);

      const isFi = IsFrequencyIntensityMethod(rsMethodRaw);
      const rsDate = searchInfo?.startDate ?? '';

      if (DEBUG_EXCEL_RISK) {
        // eslint-disable-next-line no-console
        console.log('[RiskAssessmentF][ExcelDownload1]', {
          rsMethodRaw,
          rsMethodNormalized,
          isFi,
          rsDate,
        });
      }

      const detailData = (processes || []).flatMap((p) =>
        (Array.isArray(p.details) ? p.details : []).map((d) => ({
          depNm: p.depNm ?? '',
          gongJongNm: p.processNm ?? '',
          jobNm: String(d?.rrdId ?? d?.rsdCd ?? ''),
          categoryComment: d?.categoryComment ?? '',
          todaySafety: d?.todaySafety ?? '',
          riskLevelJudgment: d?.riskLevelJudgment ?? '',
          reductionMeasures: d?.reductionMeasures ?? '',
          gaesunExDate: d?.gaesunExDate ?? '',
          gaesunComDate: d?.gaesunComDate ?? '',
          riskId: d?.riskId ?? '',
          riskAfterLevelJudgment: d?.riskAfterLevelJudgment ?? '',
          subJobId: d?.rrdId ?? '',
          rsdCd: d?.rsdCd ?? '',

          todayPossibility: d?.todayPossibility ?? '',
          todayImportance: d?.todayImportance ?? '',
          todayCurrentRisk: d?.todayCurrentRisk ?? '',
          afterPossibility: d?.afterPossibility ?? '',
          afterImportance: d?.afterImportance ?? '',
          afterCurrentRisk: d?.afterCurrentRisk ?? '',
        }))
      );

      if (!detailData.length) {
        dispatch(
          showSnackbar({
            message: '엑셀로 내보낼 데이터가 없습니다. 양식만 다운로드합니다.',
            type: 'warning',
          })
        );
      }

      const headerData = { rsMethod: rsMethodRaw, rsDate };

      if (isFi) {
        // eslint-disable-next-line no-console
        if (DEBUG_EXCEL_RISK)
          console.log('[RiskAssessmentF][ExcelDownload1] FI branch');
        await ExcelDownloadFrequencyIntensity(headerData, detailData);
      } else {
        // 기본은 3단계 판단법 (서버 RS_METHOD = '위험성 수준 3단계판단법')
        // eslint-disable-next-line no-console
        if (DEBUG_EXCEL_RISK)
          console.log('[RiskAssessmentF][ExcelDownload1] 3Step branch');
        await ExcelDownload3Step(headerData, detailData);
      }
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message ?? '엑셀 다운로드 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };

  const HandleExcelImportClick = useCallback(() => {
    excelImportInputRef.current?.click();
  }, []);

  const PromptExcelAmbiguity = useCallback((groups) => {
    return new Promise((resolve, reject) => {
      ambiguityResolverRef.current = { resolve, reject };
      setExcelAmbiguityGroups(groups);
      setExcelAmbiguityOpen(true);
    });
  }, []);

  const PromptExcelDeptSelect = useCallback((ctx) => {
    return new Promise((resolve, reject) => {
      deptSelectResolverRef.current = { resolve, reject };
      setExcelDeptSelectCtx(ctx);
      setExcelDeptSelectOpen(true);
    });
  }, []);

  const HandleExcelAmbiguityConfirm = useCallback((selections) => {
    setExcelAmbiguityOpen(false);
    setExcelAmbiguityGroups([]);
    ambiguityResolverRef.current?.resolve?.(selections ?? {});
    ambiguityResolverRef.current = null;
  }, []);

  const HandleExcelAmbiguityCancel = useCallback(() => {
    setExcelAmbiguityOpen(false);
    setExcelAmbiguityGroups([]);
    ambiguityResolverRef.current?.reject?.(new Error('cancelled'));
    ambiguityResolverRef.current = null;
  }, []);

  const HandleExcelDeptSelectConfirm = useCallback((dep) => {
    setExcelDeptSelectOpen(false);
    setExcelDeptSelectCtx(null);
    deptSelectResolverRef.current?.resolve?.(dep);
    deptSelectResolverRef.current = null;
  }, []);

  const HandleExcelDeptSelectCancel = useCallback(() => {
    setExcelDeptSelectOpen(false);
    setExcelDeptSelectCtx(null);
    deptSelectResolverRef.current?.reject?.(new Error('cancelled'));
    deptSelectResolverRef.current = null;
  }, []);

  const ExcelUpload1 = useCallback(
    async (file, importMode = 'append') => {
      if (!file) return;
      dispatch(showLoading());
      try {
        const rsMethodRaw = currentRsMethod;
        const isFi = IsFrequencyIntensityMethod(rsMethodRaw);
        const procList = Array.isArray(processes) ? processes : [];
        const rsCdNum = Number(assessmentRsCd) || 0;
        const isReplaceMode = importMode === 'replace';

        let arrayBuffer = await file.arrayBuffer();
        arrayBuffer = await RepairXlsxBufferForExcelJsLoad(arrayBuffer);
        const workbook = new ExcelJS.Workbook();
        try {
          await workbook.xlsx.load(arrayBuffer);
        } catch (loadErr) {
          throw loadErr;
        }
        const worksheet = workbook.worksheets[0];
        if (!worksheet) {
          dispatch(
            showSnackbar({
              message: '엑셀 시트를 불러오지 못했습니다.',
              type: 'warning',
            })
          );
          return;
        }

        const parsed = [];
        const startRow = 6;
        for (let r = startRow; r <= startRow + 500; r++) {
          const row = worksheet.getRow(r);
          const depNm = ExcelRiskCellToString(row.getCell(1));
          const gongJongNm = ExcelRiskCellToString(row.getCell(2));
          const categoryComment = ExcelRiskCellToString(row.getCell(3));
          const todaySafety = ExcelRiskCellToString(row.getCell(4));
          if (!depNm && !gongJongNm && !categoryComment && !todaySafety) break;

          if (isFi) {
            const todayPossibility = ExcelRiskCellToString(row.getCell(5));
            const todayImportance = ExcelRiskCellToString(row.getCell(6));
            const todayCurrentRisk = ExcelRiskCellToString(row.getCell(7));
            const reductionMeasures = ExcelRiskCellToString(row.getCell(8));
            const gaesunExDate = ExcelRiskCellToString(row.getCell(9));
            const gaesunComDate = ExcelRiskCellToString(row.getCell(10));
            const riskId = ExcelRiskCellToString(row.getCell(11));
            const afterPossibility = ExcelRiskCellToString(row.getCell(12));
            const afterImportance = ExcelRiskCellToString(row.getCell(13));
            const afterCurrentRisk = ExcelRiskCellToString(row.getCell(14));
            const excelSubJobId = ExcelRiskCellToString(row.getCell(15));
            const excelRsdCd = ExcelRiskCellToString(row.getCell(16));
            parsed.push({
              depNm,
              gongJongNm,
              categoryComment,
              todaySafety,
              todayPossibility,
              todayImportance,
              todayCurrentRisk,
              reductionMeasures,
              gaesunExDate,
              gaesunComDate,
              riskId,
              afterPossibility,
              afterImportance,
              afterCurrentRisk,
              excelSubJobId,
              excelRsdCd,
            });
          } else {
            const riskLevelJudgment = ExcelRiskCellToString(row.getCell(5));
            const reductionMeasures = ExcelRiskCellToString(row.getCell(6));
            const gaesunExDate = ExcelRiskCellToString(row.getCell(7));
            const gaesunComDate = ExcelRiskCellToString(row.getCell(8));
            const riskId = ExcelRiskCellToString(row.getCell(9));
            const riskAfterLevelJudgment = ExcelRiskCellToString(
              row.getCell(10)
            );
            const excelSubJobId = ExcelRiskCellToString(row.getCell(11));
            const excelRsdCd = ExcelRiskCellToString(row.getCell(12));
            parsed.push({
              depNm,
              gongJongNm,
              categoryComment,
              todaySafety,
              riskLevelJudgment,
              reductionMeasures,
              gaesunExDate,
              gaesunComDate,
              riskId,
              riskAfterLevelJudgment,
              excelSubJobId,
              excelRsdCd,
            });
          }
        }

        if (parsed.length === 0) {
          dispatch(
            showSnackbar({
              message:
                '엑셀에서 읽을 수 있는 데이터가 없습니다. 6행부터 공정명 등을 확인해 주세요.',
              type: 'warning',
            })
          );
          return;
        }

        const excelGroups = GroupExcelRowsByProcessNm(parsed);
        if (excelGroups.size === 0) {
          dispatch(
            showSnackbar({
              message: '엑셀에 유효한 공정명이 없습니다.',
              type: 'warning',
            })
          );
          return;
        }

        const r2Params = mapParamsBySchema(SafetyInfo_Schema.mapper.search2, {
          search: {
            custCd: searchInfo?.custCd || '',
            searchText: '',
          },
        });
        const r2Response = await apiRequest(
          'post',
          Global.urls.RiskEvaluation.getRisCustom01R2,
          r2Params
        );
        const { depData, processDataByDept } =
          MapGetRisCustom01R2ResponseToProcessModal(r2Response);
        const gongjongList = Object.values(processDataByDept || {}).flat();
        setExcelMasterDepData(Array.isArray(depData) ? depData : []);

        const ambiguousGroups = CollectAmbiguousExcelGroups(
          excelGroups,
          procList,
          gongjongList
        );

        let ambiguitySelections = {};
        if (ambiguousGroups.length > 0) {
          dispatch(closeLoading());
          try {
            ambiguitySelections = await PromptExcelAmbiguity(ambiguousGroups);
          } catch {
            return;
          }
          dispatch(showLoading());
        }

        const deptNeeded = ComputeProcessesNeedingDept(
          excelGroups,
          procList,
          gongjongList,
          depData,
          ambiguitySelections
        );
        const deptSelections = {};
        if (deptNeeded.length > 0) {
          dispatch(closeLoading());
          try {
            for (const item of deptNeeded) {
              const dep = await PromptExcelDeptSelect({
                processNm: item.processNm,
                excelDepNm: item.excelDepNm,
                depData,
              });
              deptSelections[item.processNm] = dep;
            }
          } catch {
            return;
          }
          dispatch(showLoading());
        }

        const resolvedEntries = await ResolveExcelProcessEntries({
          excelGroups,
          processes: procList,
          gongjongList,
          depData,
          ambiguitySelections,
          deptSelections,
          SaveProcessU1Async: SaveProcessRegisterU1Async,
        });

        const patchedSafety = MapGetRisCustom01R1ResponseToForm(safety01Raw, {
          assessmentTitle,
        });
        const safetyPlForU2 = patchedSafety?.processList ?? [];

        const u2ProcessList = BuildProcessListForU2(
          resolvedEntries,
          procList,
          importMode,
          safetyPlForU2
        );

        const u2Result = await SaveSafetyInfoU2Async([
          {
            rsCd: rsCdNum,
            stepSeq: WAIT_STEP_SEQ_RISK_ASSESSMENT,
            processList: u2ProcessList,
          },
        ]);
        if (IsFailed(u2Result)) {
          throw new Error(
            u2Result?.Message ??
              u2Result?.message ??
              '공정 동기화 저장에 실패했습니다.'
          );
        }

        const [safetyRefetch, searchRefetch] = await Promise.all([
          RefetchSafety01(),
          RefetchSearch1(),
        ]);

        let merged = BuildMergedProcessesFromQueries({
          safetyRaw: safetyRefetch?.data ?? safety01Raw,
          searchRawArg: searchRefetch?.data ?? searchRaw,
          assessmentTitle,
          assessmentRsMethod,
          safety01RawForMethod: safetyRefetch?.data ?? safety01Raw,
          risCustomR1Raw,
          assessmentRsCd,
          riskDetailOverlay,
          standardModelRows,
        });

        const patchedResolved = resolvedEntries.map((entry) => {
          const fresh =
            merged.find(
              (p) =>
                Norm(p.gongJongCd ?? p.processId) === Norm(entry.gongJongCd) &&
                entry.gongJongCd
            ) ??
            merged.find((p) => Norm(p.processNm) === Norm(entry.processNm));
          return {
            ...entry,
            existingProcess: fresh ?? entry.existingProcess,
          };
        });

        const { processes: withDetails, deleteCandidates, applied } =
          ApplyExcelDetailsToMergedProcesses(
            merged,
            patchedResolved,
            importMode,
            isFi,
            rsCdNum
          );

        skipNextProcessMergeRef.current = true;
        setProcesses(withDetails);
        setSelectedSriId((prev) => {
          if (prev != null && withDetails.some((p) => p.sriId === prev)) {
            return prev;
          }
          return withDetails[0]?.sriId ?? null;
        });

        if (isReplaceMode) {
          setDeletedRowsDetail((prev) => {
            const mergedDel = [...(Array.isArray(prev) ? prev : [])];
            const keySet = new Set(
              mergedDel.map(
                (r) => `${String(r?.rsdCd ?? '').trim()}|${r?.no ?? ''}`
              )
            );
            deleteCandidates.forEach((r) => {
              const key = `${String(r?.rsdCd ?? '').trim()}|${r?.no ?? ''}`;
              if (!keySet.has(key)) {
                keySet.add(key);
                mergedDel.push(r);
              }
            });
            return mergedDel;
          });
        } else {
          setDeletedRowsDetail([]);
        }
        setSelectedKeys([]);

        const actionText = isReplaceMode
          ? '기존 내역을 덮어썼습니다'
          : '신규 행으로 추가했습니다';
        const msg = `엑셀 ${parsed.length}건 중 ${applied}건을 ${actionText}. 공정 목록을 동기화했습니다.`;
        dispatch(showSnackbar({ message: msg, type: 'success' }));
      } catch (e) {
        if (String(e?.message) !== 'cancelled') {
          dispatch(
            showSnackbar({
              message: e?.message ?? '엑셀 업로드 중 오류가 발생했습니다.',
              type: 'error',
            })
          );
        }
      } finally {
        dispatch(closeLoading());
      }
    },
    [
      assessmentRsCd,
      assessmentRsMethod,
      assessmentTitle,
      currentRsMethod,
      dispatch,
      processes,
      PromptExcelAmbiguity,
      PromptExcelDeptSelect,
      RefetchSafety01,
      RefetchSearch1,
      riskDetailOverlay,
      risCustomR1Raw,
      safety01Raw,
      SaveProcessRegisterU1Async,
      SaveSafetyInfoU2Async,
      searchInfo?.custCd,
      searchRaw,
      standardModelRows,
    ]
  );

  const HandleExcelImportModeClose = useCallback(() => {
    setExcelImportModeOpen(false);
    setPendingExcelImportFile(null);
  }, []);

  const HandleExcelImportModeSelect = useCallback(
    (mode) => {
      const file = pendingExcelImportFile;
      setExcelImportModeOpen(false);
      setPendingExcelImportFile(null);
      if (!file) return;
      void ExcelUpload1(file, mode);
    },
    [ExcelUpload1, pendingExcelImportFile]
  );

  /** 근로자 의견 받기 시작 */
  const HandleWorkerOpinionReceptionStart = useCallback(
    async ({ workerOpiionsStartDate, workerOpiionsEndDate }) => {
      const custCd = searchInfo?.custCd;
      const uid = userInfo?.userid || '';

      const startStr = ToLocalDateTimeString(workerOpiionsStartDate);
      const endStr = ToLocalDateTimeString(workerOpiionsEndDate);
      if (!startStr || !endStr) {
        dispatch(
          showSnackbar({
            message: '시작·종료 일시를 확인해 주세요.',
            type: 'error',
          })
        );
        return false;
      }
      try {
        setWorkerOpinionReceptionSaveLoading(true);
        const body = {
          CustCd: Commons.EncryptAES(String(custCd)) || '',
          UserId: String(uid ?? '').trim(),
          IsRisk: 'Y',
          WorkerOpiionsStartDate: startStr,
          WorkerOpiionsEndDate: endStr,
        };
        const result = await Save2Async(body);
        if (IsFailed(result)) {
          dispatch(
            showSnackbar({
              message:
                result?.message ??
                result?.Message ??
                '근로자 의견 받기 설정 저장에 실패했습니다.',
              type: 'error',
            })
          );
          return false;
        }
        /** 근로자 의견 받기 활성화 상태 설정 */
        if (workerOpinionReceptionStorageKey) {
          sessionStorage.setItem(workerOpinionReceptionStorageKey, '1');
        }
        setWorkerOpinionReceptionActive(true);
        /** Drawer: GetInfo 재조회로 menuInfo 반영 (SuggestionList ↔ RiskNearMiss) */
        dispatch(BaseInfoActions.setRiskWorkerOpinionMenuActive(true));
        dispatch(
          showSnackbar({
            message: '근로자 의견 받기 기간이 설정되었습니다.',
            type: 'success',
          })
        );
        return true;
      } catch (e) {
        dispatch(
          showSnackbar({
            message: e?.message ?? '저장 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
        return false;
      } finally {
        setWorkerOpinionReceptionSaveLoading(false);
      }
    },
    [
      dispatch,
      searchInfo?.custCd,
      userInfo,
      workerOpinionReceptionStorageKey,
      Save2Async,
    ]
  );

  /** 근로자 의견 받기 종료 */
  const HandleWorkerOpinionReceptionEnd = useCallback(async () => {
    const custCd = searchInfo?.custCd;
    const uid = userInfo?.userid || '';

    try {
      setWorkerOpinionReceptionSaveLoading(true);
      const body = {
        CustCd: Commons.EncryptAES(String(custCd)) || '',
        UserId: String(uid ?? '').trim(),
        IsRisk: 'N',
        WorkerOpiionsStartDate: null,
        WorkerOpiionsEndDate: null,
      };
      const result = await Save2Async(body);
      if (IsFailed(result)) {
        dispatch(
          showSnackbar({
            message:
              result?.message ??
              result?.Message ??
              '의견 받기 종료 처리에 실패했습니다.',
            type: 'error',
          })
        );
        return false;
      }
      /** 근로자 의견 받기 활성화 상태 해제 */
      if (workerOpinionReceptionStorageKey) {
        sessionStorage.removeItem(workerOpinionReceptionStorageKey);
      }
      setWorkerOpinionReceptionActive(false);
      /** Drawer: GetInfo 재조회로 menuInfo 반영 */
      dispatch(BaseInfoActions.setRiskWorkerOpinionMenuActive(false));
      dispatch(
        showSnackbar({
          message: '근로자 의견 받기를 종료했습니다.',
          type: 'success',
        })
      );
      return true;
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message ?? '종료 처리 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
      return false;
    } finally {
      setWorkerOpinionReceptionSaveLoading(false);
    }
  }, [
    dispatch,
    searchInfo?.custCd,
    userInfo,
    workerOpinionReceptionStorageKey,
    Save2Async,
  ]);

  /** 근로자 의견 청취 — 선택 행을 현재 공정 세부 그리드에 추가(유해위험요인·안전보건조치·감소대책) */
  const HandleApplyWorkerOpinion = useCallback(
    (items) => {
      if (selectedSriId == null) {
        dispatch(
          showSnackbar({ message: '공정을 선택하세요.', type: 'warning' })
        );
        return false;
      }
      const list = Array.isArray(items) ? items : [];
      if (!list.length) return false;
      setProcesses((prev) =>
        prev.map((p) => {
          if (p.sriId !== selectedSriId) return p;
          let nextDetails = [...p.details];
          for (const it of list) {
            const row = CreateEmptyDetailRow(selectedSriId);
            const seq = nextDetails.length + 1;
            nextDetails.push({
              ...row,
              workerOpinionRowYn: 'Y',
              rowNo: seq,
              no: seq,
              categoryComment: it.categoryComment ?? it.riskSituation ?? '',
              todaySafety: it.todaySafety ?? it.currentMeasures ?? '',
              reductionMeasures: it.reductionMeasures ?? it.reductionPlan ?? '',
              riskImageFilePath: it.riskImageFilePath ?? it.photoBefore ?? '',
              afterRiskImageFilePath:
                it.afterRiskImageFilePath ?? it.photoAfter ?? '',
              riskImageFileName: it.riskImageFileName ?? '',
              riskImageSaveName: it.riskImageSaveName ?? '',
              afterRiskImageFileName: it.afterRiskImageFileName ?? '',
              afterRiskImageSaveName: it.afterRiskImageSaveName ?? '',
              riskImagePreviewFilePath: '',
              afterRiskImagePreviewFilePath: '',
            });
          }
          return { ...p, details: nextDetails };
        })
      );
      dispatch(
        showSnackbar({
          message: '선택한 항목이 반영되었습니다.',
          type: 'success',
        })
      );
      return true;
    },
    [selectedSriId, dispatch]
  );

  return (
    <>
      <input
        ref={excelImportInputRef}
        type="file"
        accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) {
            setPendingExcelImportFile(f);
            setExcelImportModeOpen(true);
          }
        }}
      />
      <RiskAssessment
        assessmentTitle={assessmentTitle}
        rsMethod={currentRsMethod}
        riskMatrixList={riskMatrixList}
        workflowSteps={workflowStepsFromApi}
        processList={processes}
        selectedSriId={selectedSriId}
        onSelectProcess={HandleSelectProcess}
        detailRows={currentDetails}
        onPatchRow={HandlePatchRow}
        onRequestPhoto={HandleRequestPhoto}
        onSelectedDetailKeysChange={HandleDetailSelectionKeysChange}
        selectedRowNosRef={selectedRowNosRef}
        lastCheckedIndexRef={lastCheckedIndexRef}
        detailGridRef={detailGridRef}
        DeleteNewRow={DeleteNewRow}
        RestoreRow={RestoreRow}
        handleRowSelection={HandleCheckboxRowSelection}
        onAddRow={HandleAddRow}
        onDeleteRows={HandleDeleteRows}
        onTempSave={HandleTempSave}
        onSaveAndNext={HandleSaveAndNext}
        onCancel={onCancel}
        onRefresh={HandleRefresh}
        onImport={HandleExcelImportClick}
        onExport={ExcelDownload1}
        onApplyWorkerOpinion={HandleApplyWorkerOpinion}
        riskWorkerOpinionMenuActive={riskWorkerOpinionMenuActive}
        workerOpinionReceptionActive={workerOpinionReceptionActive}
        workerOpinionReceptionSaveLoading={workerOpinionReceptionSaveLoading}
        onWorkerOpinionReceptionStart={HandleWorkerOpinionReceptionStart}
        onWorkerOpinionReceptionEnd={HandleWorkerOpinionReceptionEnd}
        loading={
          searchFetching ||
          safety01Fetching ||
          (risCustomR1MatrixEnabled && risCustomR1Fetching)
        }
        onOpenStandardModel={HandleOpenStandardModel}
        onResolveReductionMeasuresOptions={HandleResolveReductionMeasuresOptions}
      />
      <StandardModelModal
        open={standardModelOpen}
        onClose={() => setStandardModelOpen(false)}
        onConfirm={HandleConfirmStandardModel}
        rowData={modalRowData4}
        columnDefs={columnDefsStandardModel}
        gridRef={gridRef5}
        searchRef={searchRefStandardModel}
        resetAddButtonError={resetAddButtonError}
        showSnackbar={showSnackbar}
        dispatch={dispatch}
        setSelectedRowData={setSelectedModalRowData4}
        selectedRowData={selectedModalRowData4}
      />
      <PhotoReuseLayerModal
        open={photoLayerOpen}
        onClose={() => setPhotoLayerOpen(false)}
        recentPhotos={combinedRecentPhotos}
        currentPhotoPath={currentCellPhoto.path}
        currentPhotoName={currentCellPhoto.fileName}
        onPickRecent={HandlePickRecentPhoto}
        onUploadFile={async (file) => {
          const { clientKey, field } = photoLayerCtx ?? {};
          if (!clientKey || !field) return;
          await HandlePhotoPicked({ clientKey, field, file });
        }}
        sideLabel={
          photoLayerCtx?.field === 'afterRiskImageFilePath'
            ? '개선 후'
            : photoLayerCtx?.field === 'riskImageFilePath'
              ? '개선 전'
              : ''
        }
      />
      <MuiDialog
        open={excelImportModeOpen}
        onClose={HandleExcelImportModeClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              pr: 0,
            }}
          >
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              엑셀 업로드 방식을 선택하세요
            </Typography>
            <IconButton
              aria-label="닫기"
              size="small"
              onClick={HandleExcelImportModeClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Paper
              elevation={2}
              sx={{
                flex: 1,
                p: 2.5,
                borderRadius: 2,
                border: '1px solid #e3eaf2',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                기존 내역에 추가
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                현재 평가의 상세 내역은 유지하고, 엑셀 데이터를 뒤에 이어서
                등록합니다. 엑셀에만 있는 공정은 평가 전용(공정관리 미표시)으로
                자동 추가됩니다.
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2, mb: 2, flex: 1 }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  공정명 우선으로 매칭하며, 부서명이 달라도 동일 공정명이면
                  반영됩니다.
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  기존 세부 행은 그대로 유지되고, 엑셀 행이 신규로 누적됩니다.
                </Typography>
                <Typography component="li" variant="body2">
                  세부 내역은 임시저장·저장 시 서버에 반영됩니다.
                </Typography>
              </Box>
              <MuiButton
                variant="outlined"
                fullWidth
                onClick={() => HandleExcelImportModeSelect('append')}
              >
                추가로 업로드
              </MuiButton>
            </Paper>

            <Paper
              elevation={2}
              sx={{
                flex: 1,
                p: 2.5,
                borderRadius: 2,
                border: '1px solid #fff3e0',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                엑셀 기준으로 맞추기
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                엑셀 기준 공정 목록으로 맞추며, 엑셀에 없는 공정·세부는
                평가에서 제거됩니다. 공정관리 마스터는 삭제되지 않습니다.
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2, mb: 2, flex: 1 }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  엑셀에 있는 공정은 세부 내역이 교체됩니다(설비·물질 유지).
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  엑셀에 없는 공정은 이 평가에서 제외됩니다.
                </Typography>
                <Typography component="li" variant="body2">
                  엑셀에만 있는 공정은 평가 전용(공정관리 미표시)으로 자동
                  추가됩니다.
                </Typography>
              </Box>
              <MuiButton
                variant="contained"
                color="warning"
                fullWidth
                onClick={() => HandleExcelImportModeSelect('replace')}
              >
                덮어쓰기 업로드
              </MuiButton>
            </Paper>
          </Box>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
          <MuiButton onClick={HandleExcelImportModeClose}>취소</MuiButton>
        </Box>
      </MuiDialog>
      <ExcelProcessAmbiguityDialog
        open={excelAmbiguityOpen}
        groups={excelAmbiguityGroups}
        onConfirm={HandleExcelAmbiguityConfirm}
        onCancel={HandleExcelAmbiguityCancel}
      />
      <ExcelDeptSelectDialog
        open={excelDeptSelectOpen}
        processNm={excelDeptSelectCtx?.processNm ?? ''}
        excelDepNm={excelDeptSelectCtx?.excelDepNm ?? ''}
        depData={excelDeptSelectCtx?.depData ?? excelMasterDepData}
        onConfirm={HandleExcelDeptSelectConfirm}
        onCancel={HandleExcelDeptSelectCancel}
      />
    </>
  );
}

export default RiskAssessmentF;
